import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getOrderDetail, confirmOrder, updateOrderStatus, verifyOrderOtp, uploadOrderFile, getOrderFiles, deleteOrderFile, downloadOrderFile, getPrintableOrderFile, updateOrderDetails, downloadDraftInvoice, updateItemSerialNumbers, updateKitItemSerialNumbers, sendOrderWhatsAppTrigger, createInvoiceRequest, generateInvoiceViaGSTZen, getGeneratedInvoices, requestInvoiceCancellation, getOrderHistory, adjustDeliveryFee, generateEwayBill, getHubs, generateDeliveryChallan } from '../config/api';
import { toast } from "react-toastify";
import DataTable from '../components/ResponsiveDataTable';
import useIsMobile from '../hooks/useIsMobile';
import CrmCallDialog from '../components/CrmCallDialog';
import CrmWhatsappDialog from '../components/CrmWhatsappDialog';
import CodeScannerDialog from '../components/CodeScannerDialog';

const OrderDetail = () => {
  const { orderId } = useParams();
  const isMobile = useIsMobile();
  // Phones: secondary header actions fold behind a "More" toggle.
  const [headerMoreOpen, setHeaderMoreOpen] = useState(false);
  const [order, setOrder] = useState(null);
  const [callTarget, setCallTarget] = useState(null);
  const [whatsappTarget, setWhatsappTarget] = useState(null);
  const [hubs, setHubs] = useState([]);
  const [newFile, setNewFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  // Invoice dropdown
  const [invoiceDropdownOpen, setInvoiceDropdownOpen] = useState(false);
  const invoiceDropdownRef = useRef(null);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [whatsAppSendCount, setWhatsAppSendCount] = useState(() => {
    const stored = localStorage.getItem(`wa_count_${orderId}`);
    return stored ? parseInt(stored, 10) : 0;
  });
  
  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editAccess, setEditAccess] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState('Invoice');

  // Editable fields states
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    customerName: '',
    phone: '',
    gstNumber: '',
    companyNameGST: '',
    source: '',
    customerEmail: '',
    deliveryDay: '',
    shipping_address_state: '',
    shipping_address_city: '',
    shipping_address_location: '',
    shipping_address_pincode: '',
    billing_address_state: '',
    billing_address_city: '',
    billing_address_location: '',
    billing_address_pincode: '',
    vehicle_number: '',
    transporter_name: '',
    transporter_id: '',
    invoice_number: '',
    invoice_date: '',
    target_hub_id: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showB2BRejectModal, setShowB2BRejectModal] = useState(false);
  const [b2bRejectReason, setB2BRejectReason] = useState('');
  const [b2bInventoryPrompt, setB2BInventoryPrompt] = useState(null); // { shortages: [{ skuCode, available, required }] }
  const [orderHistory, setOrderHistory] = useState([]);
  const [showAddDeliveryModal, setShowAddDeliveryModal] = useState(false);
  const [addDeliveryAmount, setAddDeliveryAmount] = useState('');

  // Invoice details modal states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceModalData, setInvoiceModalData] = useState({ invoice_number: '', invoice_date: '' });
  const [savingInvoiceDetails, setSavingInvoiceDetails] = useState(false);
  const [pendingFileUpload, setPendingFileUpload] = useState(false);

  // Serial number states
  const [serialEditingItemId, setSerialEditingItemId] = useState(null);
  const [serialInputs, setSerialInputs] = useState({}); // { [itemId]: ["SN1", "SN2"] }
  const [serialSaving, setSerialSaving] = useState(false);
  const [serialModalItem, setSerialModalItem] = useState(null); // item object for modal
  const serialInputRefs = useRef([]); // refs for each serial input row

  // Kit item serial number states
  const [kitSerialEditingId, setKitSerialEditingId] = useState(null);
  const [kitSerialInputs, setKitSerialInputs] = useState({}); // { [kitItemId]: ["SN1", "SN2"] }
  const [kitSerialSaving, setKitSerialSaving] = useState(false);
  const [kitSerialModalItem, setKitSerialModalItem] = useState(null); // kit item object for modal
  const kitSerialInputRefs = useRef([]); // refs for each kit serial input row

  // Camera scanner states (QR / barcode -> serial number input).
  // scannerTarget identifies which serial list a scan should fill:
  //   { kind: 'item', itemId, max } or { kind: 'kit', kitItemId, max }
  const [scannerTarget, setScannerTarget] = useState(null);
  // Reference images captured at scan time, keyed by serial value.
  // Session-only (held in memory). NOT uploaded — display reference only.
  const [serialImages, setSerialImages] = useState({}); // { [serial]: dataUrl }
  // Full-size preview of a scan reference image (click a thumbnail to open).
  const [previewImage, setPreviewImage] = useState(null); // { src, serial }

  // Invoice request/approval states
  const [invoiceRequestLoading, setInvoiceRequestLoading] = useState(false);

  // PII reveal states
  const [revealedPhone, setRevealedPhone] = useState(null);
  const [revealedEmail, setRevealedEmail] = useState(null);
  const [piiLoading, setPiiLoading] = useState({});
  const loggedInUser = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
  const userRole = loggedInUser.role;
  const loggedInUserId = loggedInUser.id;
  const hubManagerLimitedEdit =
    loggedInUser.access?.includes('hubManager') &&
    userRole !== 'admin' &&
    userRole !== 'superAdmin';
  const [showInvoiceGenerateModal, setShowInvoiceGenerateModal] = useState(false);
  const [accountsAccess, setAccountsAccess] = useState(false);
  const [hubManagerAccess, setHubManagerAccess] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [ewayLoading, setEwayLoading] = useState(false);
  const [printingFileId, setPrintingFileId] = useState(null);

  useEffect(() => {

    if (!orderId || !/^[A-Za-z0-9_-]+$/.test(orderId)) {
      setLoading(false);
      toast.error('Invalid order ID');
      return;
    }

    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    if (userData.role == 'superAdmin' || userData.role == 'admin' || userData.access?.includes('hubManager')) {
      setEditAccess(true);
    }
    if (userData.role === 'superAdmin' || userData.access?.includes('finance')) {
      setAccountsAccess(true);
    }
    if (userData.access?.includes('hubManager')) {
      setHubManagerAccess(true);
    }
    loadOrderDetail();
    loadOrderFiles();
    loadOrderHistory();
    loadHubs();
  }, [orderId]);

  const loadHubs = async () => {
    try {
      const res = await getHubs();
      setHubs(res?.data || []);
    } catch (err) {
      // Non-fatal: the Target Hub dropdown will just have no options.
      console.error('Failed to load hubs:', err);
    }
  };

  const loadGeneratedInvoice = async () => {
    try {
      const res = await getGeneratedInvoices({ order_id: orderId });
      const invoices = res?.data || [];
      // Get the latest GSTZen invoice for this order
      const gstzenInvoice = invoices.find(inv => inv.generation_method === 'gstzen' && inv.cancellation_status !== 'approved');
      console.log("gstzenInvoice", gstzenInvoice);
      setGeneratedInvoice(gstzenInvoice || null);
      // The invoice API reconciles legacy E-Way PDFs into order_files.
      // Reload afterward so Order Documents reflects the repaired/new row.
      await loadOrderFiles();
    } catch (err) {
      // silently fail
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (invoiceDropdownRef.current && !invoiceDropdownRef.current.contains(e.target)) {
        setInvoiceDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadOrderDetail = async () => {
    setLoading(true);
    setRevealedPhone(null);
    setRevealedEmail(null);
    try {
      let data = await getOrderDetail(orderId) || {};
      data = data.data || {};
      setOrder(data);
      setNewStatus(data.status);
      setOtpVerified(data.status === "delivered");
      
      // Load generated invoice for cancellation check
      if (data.invoice_request_status === 'generated') {
        loadGeneratedInvoice();
      }
      
      // Initialize editable data
      setEditedData({
        customerName: data.customerName || '',
        phone: data.phone || '',
        gstNumber: data.gstNumber || '',
        companyNameGST: data.companyNameGST || '',
        source: data.source || '',
        customerEmail: data.customerEmail || '',
        deliveryDay: data.deliveryDay || '',
        shipping_address_state: data.shipping_address_state || '',
        shipping_address_city: data.shipping_address_city || '',
        shipping_address_location: data.shipping_address_location || '',
        shipping_address_pincode: data.shipping_address_pincode || '',
        billing_address_state: data.billing_address_state || '',
        billing_address_city: data.billing_address_city || '',
        billing_address_location: data.billing_address_location || '',
        billing_address_pincode: data.billing_address_pincode || '',
        vehicle_number: data.vehicle_number || '',
        transporter_name: data.transporter_name || '',
        transporter_id: data.transporter_id || '',
        invoice_number: data.invoice_number || '',
        invoice_date: data.invoice_date || '',
        target_hub_id: data.target_hub_id != null ? String(data.target_hub_id) : ''
      });
    } catch (error) {
      console.error('Error loading order detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrderFiles = async () => {
    try {
      const files = await getOrderFiles(orderId) || {};
      setUploadedFiles(files.data || []);
    } catch (error) {
      console.error('Error loading order files:', error);
    }
  };

  const handleRevealPii = async (fieldType) => {
    setPiiLoading(prev => ({ ...prev, [fieldType]: true }));
    try {
      const { apiCall, API_BASE_URL } = await import('../config/api');
      const res = await apiCall(`${API_BASE_URL}/orders/oms/reveal-pii`, {
        method: 'POST',
        body: JSON.stringify({ orderId, fieldType })
      });
      if (res.success) {
        if (fieldType === 'phone') setRevealedPhone(res.data.value);
        else setRevealedEmail(res.data.value);
        if (res.data.remaining !== undefined && res.data.remaining <= 2) {
          toast.warn(`Only ${res.data.remaining} PII views remaining today`);
        }
      } else {
        toast.error(res.message || 'Unable to reveal');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to reveal');
    } finally {
      setPiiLoading(prev => ({ ...prev, [fieldType]: false }));
    }
  };

  const handleCancelAndRefund = async () => {
    if (!window.confirm('Cancel this order and refund the full amount to wallet? This action cannot be undone.')) return;
    setUpdating(true);
    try {
      const { apiCall, API_BASE_URL } = await import('../config/api');
      const res = await apiCall(`${API_BASE_URL}/orders/oms/cancelAndRefund`, {
        method: 'POST',
        body: JSON.stringify({ orderId })
      });
      if (res.success) {
        toast.success(res.message || 'Order cancelled and refunded');
        loadOrderDetail();
      } else {
        toast.error(res.message || 'Failed to cancel');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to cancel and refund');
    } finally {
      setUpdating(false);
    }
  };

  const loadOrderHistory = async () => {
    try {
      const res = await getOrderHistory(orderId);
      if (res.success) setOrderHistory(res.data || []);
    } catch (error) {
      console.error('Error loading order history:', error);
    }
  };

  const handleRemoveDeliveryFee = async () => {
    const totalFee = parseFloat(order.deliveryCharges || 0) + parseFloat(order.unloadingCharges || 0);
    if (!window.confirm(`Remove delivery fee (₹${totalFee}) and refund to wallet?`)) return;
    try {
      const res = await adjustDeliveryFee(orderId, 'remove');
      if (res.success) {
        toast.success('Delivery fee removed and refunded');
        loadOrderDetail();
        loadOrderHistory();
      } else {
        toast.error(res.message || 'Failed');
      }
    } catch (err) { toast.error(err.message || 'Failed to remove delivery fee'); }
  };

  const handleAddDeliveryFee = async () => {
    if (!addDeliveryAmount || parseFloat(addDeliveryAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    try {
      const res = await adjustDeliveryFee(orderId, 'add', parseFloat(addDeliveryAmount));
      if (res.success) {
        toast.success('Delivery fee added and deducted from wallet');
        setShowAddDeliveryModal(false);
        setAddDeliveryAmount('');
        loadOrderDetail();
        loadOrderHistory();
      } else {
        toast.error(res.message || 'Failed');
      }
    } catch (err) { toast.error(err.message || 'Failed to add delivery fee'); }
  };

  const handleConfirmOrder = async () => {
    if (!order || order.confirmed) return;
    
    setUpdating(true);
    try {
      let apiRes = await confirmOrder(orderId);
      if(apiRes && apiRes.data && apiRes.success) {
        order.confirmed = true;
        setOrder(order);
      }
    } catch (error) {
      console.error('Error confirming order:', error);
      alert('Failed to confirm order');
    } finally {
      setUpdating(false);
    }
  };

  // Core B2B confirm call. Shared by the initial confirm and the
  // "proceed anyway" path after an inventory-shortage prompt.
  // - Wallet shortage stays a hard block (backend rejects, we just toast).
  // - Inventory shortage is a soft block: backend returns success:false with
  //   code 'INVENTORY_SHORTAGE'; we surface a prompt instead of a plain error.
  const confirmB2BOrder = async ({ ignoreInventory = false } = {}) => {
    setUpdating(true);
    try {
      const { apiCall, API_BASE_URL } = await import('../config/api');
      const res = await apiCall(`${API_BASE_URL}/b2b/oms/orders/confirm`, {
        method: 'POST', body: JSON.stringify({ orderId, ignoreInventory })
      });
      if (res.success) {
        setB2BInventoryPrompt(null);
        toast.success('B2B order confirmed successfully');
        loadOrderDetail();
        return;
      }
      // Soft inventory block — ask the user whether to proceed anyway.
      if (res.errors?.code === 'INVENTORY_SHORTAGE') {
        setB2BInventoryPrompt({ shortages: res.errors.shortages || [] });
        return;
      }
      toast.error(res.message || 'Failed to confirm');
    } catch (err) {
      toast.error(err.message || 'Failed to confirm B2B order');
    } finally { setUpdating(false); }
  };

  const handleB2BConfirm = async () => {
    if (!window.confirm('Confirm this B2B order? This will debit the balance amount from the buyer\'s wallet.')) return;
    await confirmB2BOrder({ ignoreInventory: false });
  };

  const handleB2BConfirmIgnoreInventory = async () => {
    await confirmB2BOrder({ ignoreInventory: true });
  };

  const handleB2BReject = async () => {
    setUpdating(true);
    try {
      const { apiCall, API_BASE_URL } = await import('../config/api');
      const res = await apiCall(`${API_BASE_URL}/b2b/oms/orders/reject`, {
        method: 'POST', body: JSON.stringify({ orderId, reason: b2bRejectReason })
      });
      if (res.success) {
        toast.success('B2B order rejected, advance refunded');
        setShowB2BRejectModal(false);
        setB2BRejectReason('');
        loadOrderDetail();
      } else {
        toast.error(res.message || 'Failed to reject');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to reject B2B order');
    } finally { setUpdating(false); }
  };

  const handleStatusChange = async () => {
    if (!order || newStatus === order.status) return;
    
    setUpdating(true);
    try {
      let apiRes = await updateOrderStatus(orderId, newStatus);
      if(apiRes && apiRes.data && apiRes.success) {
        order.status = newStatus;
        setOrder(order);
      }
      setOrder(order);
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      setOtpError("Please enter OTP");
      return;
    }

    setVerifyingOtp(true);
    setOtpError("");

    try {
      const apiRes = await verifyOrderOtp({
        orderId,
        otp,
      });

      if (apiRes?.data?.error) {
        setOtpError(apiRes.data.error);
        setOtpVerified(false);
        return;
      }

      if (apiRes?.success) {
        setOtpVerified(true);
        setOtp("");
        toast.success("OTP verified successfully ✅");
      } else {
        setOtpError(apiRes?.message || "Invalid OTP");
      }
    } catch (err) {
      console.error("OTP verification failed", err);
      setOtpError("OTP verification failed");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setValidationErrors({});
    // Reset to original values
    setEditedData({
      customerName: order.customerName || '',
      phone: order.phone || '',
      gstNumber: order.gstNumber || '',
      companyNameGST: order.companyNameGST || '',
      source: order.source || '',
      customerEmail: order.customerEmail || '',
      deliveryDay: order.deliveryDay || '',
      shipping_address_state: order.shipping_address_state || '',
      shipping_address_city: order.shipping_address_city || '',
      shipping_address_location: order.shipping_address_location || '',
      shipping_address_pincode: order.shipping_address_pincode || '',
      billing_address_state: order.billing_address_state || '',
      billing_address_city: order.billing_address_city || '',
      billing_address_location: order.billing_address_location || '',
      billing_address_pincode: order.billing_address_pincode || '',
      vehicle_number: order.vehicle_number || '',
      transporter_name: order.transporter_name || '',
      transporter_id: order.transporter_id || '',
      invoice_number: order.invoice_number || '',
      invoice_date: order.invoice_date || '',
      target_hub_id: order.target_hub_id != null ? String(order.target_hub_id) : ''
    });
  };

  const handleSaveEdit = async () => {
    const errors = {};
    const isAdminOrSuper = userRole === 'admin' || userRole === 'superAdmin';

    // Validate phone number if provided (only for admin/superAdmin who can edit it)
    if (isAdminOrSuper && editedData.phone && !/^\d{10}$/.test(editedData.phone)) {
      errors.phone = 'Phone number must be exactly 10 digits';
    }

    // Hub Managers cannot edit GST details.
    if (!hubManagerLimitedEdit && editedData.gstNumber && !validateGSTNumber(editedData.gstNumber)) {
      errors.gstNumber = 'Invalid GST number format. Format: 22AAAAA0000A1Z5';
    }

    // Transporter ID follows the GSTIN format.
    if (editedData.transporter_id && !validateGSTNumber(editedData.transporter_id)) {
      errors.transporter_id = 'Invalid Transporter ID. It must be a valid GSTIN. Format: 22AAAAA0000A1Z5';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      Object.values(errors).forEach(error => toast.error(error));
      return;
    }

    setValidationErrors({});
    setUpdating(true);
    try {
      // Map display keys to database keys for API call
      const addressAndVehicleFields = {
        shipping_address_state: editedData.shipping_address_state,
        shipping_address_city: editedData.shipping_address_city,
        shipping_address_location: editedData.shipping_address_location,
        shipping_address_pincode: editedData.shipping_address_pincode,
        billing_address_state: editedData.billing_address_state,
        billing_address_city: editedData.billing_address_city,
        billing_address_location: editedData.billing_address_location,
        billing_address_pincode: editedData.billing_address_pincode,
        vehicle_number: editedData.vehicle_number,
        transporter_id: editedData.transporter_id
      };

      const updatePayload = hubManagerLimitedEdit ? addressAndVehicleFields : {
        ...addressAndVehicleFields,
        user_name: editedData.customerName,
        gstNumber: editedData.gstNumber,
        companyNameGST: editedData.companyNameGST,
        source: editedData.source,
        deliveryDay: editedData.deliveryDay,
        invoice_number: editedData.invoice_number,
        invoice_date: editedData.invoice_date || null
      };

      // Target hub is only editable by accounts users (finance/superAdmin) and
      // only relevant for hub-to-hub orders. Hub managers never send it.
      if (!hubManagerLimitedEdit && accountsAccess && editedData.source === 'hub to hub') {
        updatePayload.target_hub_id = editedData.target_hub_id || null;
      }

      // Only include phone/email for admin/superAdmin (non-admin gets masked values)
      if (isAdminOrSuper) {
        updatePayload.user_phone = editedData.phone;
        updatePayload.user_email = editedData.customerEmail;
      }
      
      await updateOrderDetails(orderId, updatePayload);
      toast.success("Order details updated successfully!");
      setIsEditing(false);
      await loadOrderDetail(); // Reload order data
    } catch (error) {
      console.error('Error updating order details:', error);
      toast.error(error.response?.data?.message || 'Failed to update order details');
    } finally {
      setUpdating(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateGSTNumber = (gstNumber) => {
    if (!gstNumber) {
      return true; // Empty is valid (optional field)
    }
    
    // GST format: 2 digits (state code) + 10 alphanumeric (PAN) + 1 digit (entity number) + 1 letter (Z) + 1 alphanumeric (checksum)
    // Example: 22AAAAA0000A1Z5
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    
    return gstRegex.test(gstNumber.toUpperCase());
  };

  const handleSerialEdit = (item) => {
    setSerialEditingItemId(item.id);
    setSerialModalItem(item);
    setSerialInputs(prev => ({
      ...prev,
      [item.id]: item.serialNumbers && item.serialNumbers.length > 0 ? [...item.serialNumbers] : ['']
    }));
  };

  const handleSerialView = (item) => {
    setSerialEditingItemId(null);
    setSerialModalItem(item);
  };

  const handleSerialModalClose = () => {
    setSerialModalItem(null);
    setSerialEditingItemId(null);
  };

  const handleSerialInputChange = (itemId, index, value) => {
    setSerialInputs(prev => {
      const updated = [...(prev[itemId] || [''])];
      updated[index] = value;
      return { ...prev, [itemId]: updated };
    });
  };

  // Auto-add next row when typing in the last input, respecting max allowed serials
  const handleSerialInputChangeWithAutoAdd = (itemId, index, value, maxSerials) => {
    setSerialInputs(prev => {
      const updated = [...(prev[itemId] || [''])];
      updated[index] = value;
      const isLastRow = index === updated.length - 1;
      const belowMax = maxSerials == null || updated.length < maxSerials;
      if (isLastRow && value.trim() !== '' && belowMax) {
        updated.push('');
      }
      return { ...prev, [itemId]: updated };
    });
  };

  const handleAddSerialInput = (itemId) => {
    setSerialInputs(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || ['']), '']
    }));
  };

  const handleRemoveSerialInput = (itemId, index) => {
    setSerialInputs(prev => {
      const updated = [...(prev[itemId] || [''])];
      updated.splice(index, 1);
      return { ...prev, [itemId]: updated.length > 0 ? updated : [''] };
    });
  };

  const handleSerialSave = async (itemId) => {
    const serials = (serialInputs[itemId] || []).map(s => s.trim()).filter(Boolean);
    setSerialSaving(true);
    try {
      const res = await updateItemSerialNumbers(itemId, serials);
      if (!res || !res.success) {
        toast.error(res?.message || 'Failed to save serial numbers');
        return;
      }
      setOrder(prev => ({
        ...prev,
        items: prev.items.map(it => it.id === itemId ? { ...it, serialNumbers: serials } : it)
      }));
      setSerialEditingItemId(null);
      setSerialModalItem(null);
      toast.success('Serial numbers saved!');
    } catch (err) {
      toast.error('Failed to save serial numbers');
    } finally {
      setSerialSaving(false);
    }
  };

  const handleSerialCancel = () => {
    setSerialEditingItemId(null);
  };

  // Kit item serial number handlers
  const handleKitSerialEdit = (kitItem, parentItemId) => {
    setKitSerialEditingId(kitItem.id);
    setKitSerialModalItem({ ...kitItem, parentItemId });
    setKitSerialInputs(prev => ({
      ...prev,
      [kitItem.id]: kitItem.serialNumbers && kitItem.serialNumbers.length > 0 ? [...kitItem.serialNumbers] : ['']
    }));
  };

  const handleKitSerialView = (kitItem, parentItemId) => {
    setKitSerialEditingId(null);
    setKitSerialModalItem({ ...kitItem, parentItemId });
  };

  const handleKitSerialModalClose = () => {
    setKitSerialModalItem(null);
    setKitSerialEditingId(null);
  };

  const handleKitSerialInputChange = (kitItemId, index, value) => {
    setKitSerialInputs(prev => {
      const updated = [...(prev[kitItemId] || [''])];
      updated[index] = value;
      // Auto-add next row when typing in the last input (respect max qty)
      const maxSerials = kitSerialModalItem ? kitSerialModalItem.qty : null;
      if (index === updated.length - 1 && value.trim() !== '' && (maxSerials == null || updated.length < maxSerials)) {
        updated.push('');
      }
      return { ...prev, [kitItemId]: updated };
    });
  };

  const handleKitSerialRemoveInput = (kitItemId, index) => {
    setKitSerialInputs(prev => {
      const updated = [...(prev[kitItemId] || [''])];
      updated.splice(index, 1);
      return { ...prev, [kitItemId]: updated.length > 0 ? updated : [''] };
    });
  };

  const handleKitSerialAddInput = (kitItemId) => {
    const maxSerials = kitSerialModalItem ? kitSerialModalItem.qty : null;
    setKitSerialInputs(prev => {
      const current = prev[kitItemId] || [''];
      if (maxSerials != null && current.length >= maxSerials) return prev;
      return { ...prev, [kitItemId]: [...current, ''] };
    });
  };

  const handleKitSerialSave = async (kitItemId, parentItemId) => {
    const serials = (kitSerialInputs[kitItemId] || []).map(s => s.trim()).filter(Boolean);
    setKitSerialSaving(true);
    try {
      const res = await updateKitItemSerialNumbers(kitItemId, serials);
      if (!res || !res.success) {
        toast.error(res?.message || 'Failed to save serial numbers');
        return;
      }
      // Update local state
      setOrder(prev => ({
        ...prev,
        items: prev.items.map(it => {
          if (it.id === parentItemId && it.kitItems) {
            return {
              ...it,
              kitItems: it.kitItems.map(ki => ki.id === kitItemId ? { ...ki, serialNumbers: serials } : ki)
            };
          }
          return it;
        })
      }));
      setKitSerialEditingId(null);
      setKitSerialModalItem(null);
      toast.success('Serial numbers saved!');
    } catch (err) {
      toast.error('Failed to save serial numbers');
    } finally {
      setKitSerialSaving(false);
    }
  };

  const handleKitSerialCancel = () => {
    setKitSerialEditingId(null);
  };

  // --- Camera scan -> serial number ---------------------------------------
  // Fills a scanned code into the first empty slot of the target serial list.
  // Reuses the existing serialInputs / kitSerialInputs state so manual typing,
  // duplicate detection, auto-add and save behaviour stay exactly the same.
  const applyScannedSerial = (list, value, max) => {
    const code = (value || '').trim();
    if (!code) return list;
    const current = Array.isArray(list) && list.length > 0 ? [...list] : [''];
    // Ignore if already present (scanning the same panel twice).
    if (current.some((s) => s.trim() === code)) return current;
    // Count how many real serials are already filled.
    const filledCount = current.filter((s) => s.trim() !== '').length;
    if (max != null && filledCount >= max) return current; // reached quantity
    // Put the code into the first empty slot, else append.
    const emptyIdx = current.findIndex((s) => s.trim() === '');
    if (emptyIdx !== -1) {
      current[emptyIdx] = code;
    } else {
      current.push(code);
    }
    // Keep one trailing empty row for manual entry, unless we've hit max.
    const nowFilled = current.filter((s) => s.trim() !== '').length;
    const hasEmpty = current.some((s) => s.trim() === '');
    if (!hasEmpty && (max == null || nowFilled < max)) {
      current.push('');
    }
    return current;
  };

  const handleScannedCode = (value, image) => {
    if (!scannerTarget) return;
    const code = (value || '').trim();
    if (!code) return;
    const { kind, itemId, kitItemId, max } = scannerTarget;
    // Keep the captured reference image keyed by serial value. Session-only,
    // never sent to the backend (backend stores just the serial string).
    if (image) {
      setSerialImages((prev) => (prev[code] ? prev : { ...prev, [code]: image }));
    }
    if (kind === 'item') {
      let added = false;
      setSerialInputs((prev) => {
        const before = prev[itemId] || [''];
        const next = applyScannedSerial(before, code, max);
        added = next !== before && JSON.stringify(next) !== JSON.stringify(before);
        return { ...prev, [itemId]: next };
      });
      if (!added) toast.info('Serial already added or quantity reached');
    } else if (kind === 'kit') {
      let added = false;
      setKitSerialInputs((prev) => {
        const before = prev[kitItemId] || [''];
        const next = applyScannedSerial(before, code, max);
        added = next !== before && JSON.stringify(next) !== JSON.stringify(before);
        return { ...prev, [kitItemId]: next };
      });
      if (!added) toast.info('Serial already added or quantity reached');
    }
  };

  const openItemScanner = (item) => {
    const isKit = item.categoryId === 4;
    setScannerTarget({ kind: 'item', itemId: item.id, max: isKit ? null : item.quantity });
  };

  const openKitScanner = (kitItem) => {
    setScannerTarget({ kind: 'kit', kitItemId: kitItem.id, max: kitItem.qty });
  };

  const handleRequestCancellation = async () => {
    if (!window.confirm('Request cancellation for this invoice? This can only be done within 24 hours.')) return;
    setCancellationLoading(true);
    try {
      await requestInvoiceCancellation(generatedInvoice.id);
      toast.success('Cancellation request submitted');
      await loadGeneratedInvoice();
    } catch (err) {
      toast.error(err.message || 'Failed to request cancellation');
    } finally {
      setCancellationLoading(false);
    }
  };

  // Shared e-way runner: calls `generate(distance, transporterName)` and, on the
  // two known recoverable errors, prompts the user and retries once each:
  //   - NEED_TRANSPORTER_NAME: ask for the transporter name (vehicle-less path).
  //   - distance unavailable:  ask for the distance in KM.
  // Throws { handled: true } if the user cancels a prompt (caller stays silent).
  const runEwayGeneration = async (generate) => {
    let distance = null;
    let transporterName = null;
    // Up to 2 recoverable retries (one per prompt type).
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await generate(distance, transporterName);
        return;
      } catch (err) {
        const msg = (err?.message || '').toLowerCase();
        if (err?.code === 'NEED_TRANSPORTER_NAME' && !transporterName) {
          const name = window.prompt('Transporter name could not be fetched automatically. Please enter the transporter name:');
          if (!name || !name.trim()) { const e = new Error('cancelled'); e.handled = true; throw e; }
          transporterName = name.trim();
          continue;
        }
        if (msg.includes('distance') && !distance) {
          const d = window.prompt('Distance between pincodes is not available. Please enter distance in KM:');
          if (!d || !(+d > 0)) { const e = new Error('cancelled'); e.handled = true; throw e; }
          distance = +d;
          continue;
        }
        throw err;
      }
    }
  };

  const handleGenerateEwayBill = async () => {
    if (!generatedInvoice) return;
    setEwayLoading(true);
    try {
      await runEwayGeneration((distance, transporterName) =>
        generateEwayBill(generatedInvoice.id, distance, transporterName)
      );
      toast.success('E-way bill generated successfully');
      await loadGeneratedInvoice();
    } catch (err) {
      if (err?.handled) {
        // User cancelled a prompt — stay silent.
      } else {
        toast.error(err?.message || 'Failed to generate e-way bill');
      }
    } finally {
      setEwayLoading(false);
    }
  };

  const handleRequestInvoice = async () => {
    setInvoiceRequestLoading(true);
    try {
      await createInvoiceRequest(orderId);
      toast.success('Invoice request created successfully!');
      await loadOrderDetail();
    } catch (error) {
      const msg = error.message || 'Failed to request invoice';
      toast.error(msg, {
        autoClose: msg.length > 80 ? 8000 : 4000
      });
    } finally {
      setInvoiceRequestLoading(false);
    }
  };

  const handleApproveAndGenerateInvoice = async () => {
    // Invoice number is generated automatically on the backend.
    // Hub Manager access generates directly (no request/approve step) for any source.
    // Hub-to-hub orders also generate directly (accounts users only) — there is
    // no request/approve step for them.
    const isHubToHubOrder = order?.source === 'hub to hub';
    setInvoiceRequestLoading(true);
    try {
      await generateInvoiceViaGSTZen(orderId, null, { direct: hubManagerAccess || isHubToHubOrder });
      toast.success('Invoice generated successfully!');
      setShowInvoiceGenerateModal(false);
      await loadOrderDetail();
      await loadOrderHistory();
    } catch (error) {
      toast.error(error.message || 'Failed to generate invoice');
    } finally {
      setInvoiceRequestLoading(false);
    }
  };

  const handleGenerateDeliveryChallan = async () => {
    // Intrastate hub-to-hub: generate a Delivery Challan (no tax invoice).
    setInvoiceRequestLoading(true);
    try {
      await generateDeliveryChallan(orderId);
      toast.success('Delivery Challan generated successfully!');
      setShowInvoiceGenerateModal(false);
      await loadOrderDetail();
      await loadOrderHistory();
    } catch (error) {
      toast.error(error.message || 'Failed to generate Delivery Challan');
    } finally {
      setInvoiceRequestLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    setGeneratingInvoice(true);
    try {
      const response = await downloadDraftInvoice(orderId);
      
      if (response && response.success) {
        toast.success(response.message || 'Invoice downloaded successfully!');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      
      // Check if it's a validation error
      if (error.response && error.response.data && error.response.data.message) {
        const errorMessage = error.response.data.message;
        
        if (errorMessage.includes('Missing required fields')) {
          // Extract missing fields from error message
          const missingFields = errorMessage.replace('Missing required fields: ', '');
          toast.error(`Cannot generate invoice. Missing required fields: ${missingFields}`, {
            autoClose: 5000
          });
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error('Failed to download invoice');
      }
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleSendWhatsAppTrigger = async () => {
    if (whatsAppSendCount >= 1) return;

    setSendingWhatsApp(true);
    try {
      await sendOrderWhatsAppTrigger(orderId);
      toast.success('WhatsApp trigger sent successfully!');
      const newCount = whatsAppSendCount + 1;
      setWhatsAppSendCount(newCount);
      localStorage.setItem(`wa_count_${orderId}`, newCount.toString());
    } catch (error) {
      console.error('Error sending invoice:', error);
      
      if (error.response && error.response.data && error.response.data.message) {
        const errorMessage = error.response.data.message;
        
        if (errorMessage.includes('Missing required fields')) {
          // Extract missing fields from error message
          const missingFields = errorMessage.replace('Missing required fields: ', '');
          toast.error(`Cannot send invoice. Missing required fields: ${missingFields}`, {
            autoClose: 5000
          });
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error(error.message);
      }
    } finally {
      setSendingWhatsApp(false);
      setInvoiceDropdownOpen(false);
    }
  };

  const handleFileChange = (e) => {
    setNewFile(e.target.files[0]);
  }

  const handleFileUpload = async () => {
    const file = newFile;
    if (!file) return;

    // If uploading an Invoice, check that invoice_number and invoice_date exist
    if (selectedFileType === 'Invoice' && (!order.invoice_number || !order.invoice_date)) {
      setInvoiceModalData({
        invoice_number: order.invoice_number || '',
        invoice_date: order.invoice_date || ''
      });
      setPendingFileUpload(true);
      setShowInvoiceModal(true);
      return;
    }

    await doFileUpload(file);
  };

  const handleSaveInvoiceDetails = async () => {
    if (!invoiceModalData.invoice_number || !invoiceModalData.invoice_date) {
      toast.error('Please fill in both Invoice Number and Invoice Date');
      return;
    }

    setSavingInvoiceDetails(true);
    try {
      const updatePayload = {
        user_name: order.customerName,
        user_phone: order.phone,
        gstNumber: order.gstNumber,
        companyNameGST: order.companyNameGST,
        source: order.source,
        user_email: order.customerEmail,
        deliveryDay: order.deliveryDay,
        shipping_address_state: order.shipping_address_state,
        shipping_address_city: order.shipping_address_city,
        shipping_address_location: order.shipping_address_location,
        shipping_address_pincode: order.shipping_address_pincode,
        billing_address_state: order.billing_address_state,
        billing_address_city: order.billing_address_city,
        billing_address_location: order.billing_address_location,
        billing_address_pincode: order.billing_address_pincode,
        vehicle_number: order.vehicle_number,
        transporter_name: order.transporter_name,
        transporter_id: order.transporter_id,
        invoice_number: invoiceModalData.invoice_number,
        invoice_date: invoiceModalData.invoice_date
      };

      await updateOrderDetails(orderId, updatePayload);
      toast.success('Invoice details saved!');
      setShowInvoiceModal(false);

      // Update local order state so subsequent checks pass
      setOrder(prev => ({
        ...prev,
        invoice_number: invoiceModalData.invoice_number,
        invoice_date: invoiceModalData.invoice_date
      }));
      setEditedData(prev => ({
        ...prev,
        invoice_number: invoiceModalData.invoice_number,
        invoice_date: invoiceModalData.invoice_date
      }));

      // Proceed with the pending file upload
      if (pendingFileUpload && newFile) {
        await doFileUpload(newFile);
      }
    } catch (error) {
      console.error('Error saving invoice details:', error);
      toast.error(error.response?.data?.message || 'Failed to save invoice details');
    } finally {
      setSavingInvoiceDetails(false);
      setPendingFileUpload(false);
    }
  };

  const doFileUpload = async (file) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, JPEG, and PNG files are allowed');
      return;
    }

    setUploading(true);
    try {
      const fileData = {
        fileName: file.name,
        fileType: selectedFileType,
        fileSize: formatFileSize(file.size),
        uploadedBy: 'Current User',
        file: file
      };

      const uploaded = await uploadOrderFile(orderId, fileData);
      setUploadedFiles(prev => [...prev, uploaded]);
      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await deleteOrderFile(orderId, fileId);
      setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
      toast.success('File deleted successfully!');
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  const handleDownloadFile = async (file) => {
    // Mock download - replace with actual download logic
    await downloadOrderFile(orderId, file.id);
    toast.info(`Downloading ${file.file_name}...`);
  };

  const handlePrintFile = async (file) => {
    setPrintingFileId(file.id);
    try {
      const blob = await getPrintableOrderFile(orderId, file.id);
      if (!blob) return;
      const url = window.URL.createObjectURL(blob);
      const frame = document.createElement('iframe');
      frame.style.position = 'fixed';
      frame.style.right = '0';
      frame.style.bottom = '0';
      frame.style.width = '1px';
      frame.style.height = '1px';
      frame.style.border = '0';
      frame.setAttribute('title', 'Print document');
      frame.onload = () => {
        setTimeout(() => {
          try {
            frame.contentWindow.focus();
            frame.contentWindow.print();
          } catch (_) {
            window.open(url, '_blank');
          }
        }, 250);
      };
      frame.src = url;
      document.body.appendChild(frame);
      setTimeout(() => {
        frame.remove();
        window.URL.revokeObjectURL(url);
      }, 120000);
    } catch (error) {
      toast.error(error.message || 'Could not print this document');
    } finally {
      setPrintingFileId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeLabel = (type) => {
    const types = {
      'Invoice': 'Invoice',
      'E-Way Bill': 'E-Way Bill',
      'DCR': 'DCR',
      'DC': 'DC'
    };
    return types[type] || type;
  };

  const getFileTypeBadgeClass = (type) => {
    const classes = {};
    return classes[type] || 'status-badge';
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'confirmed':
        return 'status-badge status-confirmed';
      case 'pending':
        return 'status-badge status-pending';
      case 'shipped':
        return 'status-badge status-shipped';
      case 'delivered':
        return 'status-badge status-delivered';
      default:
        return 'status-badge';
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#495057' }}>
            Loading order details...
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
          <h1 style={{ color: '#dc3545', marginBottom: '16px' }}>Order Not Found</h1>
          <p style={{ color: '#6c757d', marginBottom: '30px', fontSize: '16px' }}>
            The order with ID <strong>{orderId}</strong> could not be found.
          </p>
          <Link to="/orders" className="btn btn-primary">
            ← Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  const pendingActions = [];
  
  // Check if not shipped
  if (order.status !== 'shipped' && order.status !== 'delivered') {
    pendingActions.push('Not shipped');
  }
  
  // Check if not delivered (OTP verification pending)
  if (order.status !== 'delivered') {
    pendingActions.push('Not Delivered (OTP verification pending)');
  }
  
  // Check if invoice is uploaded
  const hasInvoice = uploadedFiles.some(file => file.file_type === 'Invoice');
  if (!hasInvoice) {
    pendingActions.push('Invoice upload pending');
  }
  
  // Check if DCR is required and uploaded
  const dcrRequired = order.items.some(item => item.dcrApplicable === true);
  if (dcrRequired) {
    const hasDCR = uploadedFiles.some(file => file.file_type === 'DCR');
    if (!hasDCR) {
      pendingActions.push('DCR pending');
    }
  }

  // Phones: "Open in Maps" links under the addresses.
  const shippingAddressText = [
    order.shipping_address_location,
    order.shipping_address_city,
    order.shipping_address_state,
    order.shipping_address_pincode
  ].filter(Boolean).join(', ');
  const billingAddressText = [
    order.billing_address_location,
    order.billing_address_city,
    order.billing_address_state,
    order.billing_address_pincode
  ].filter(Boolean).join(', ');

  return (
    <div className="container">
      <div className="card">
        <div className="order-detail-header">
          <div className="order-detail-title">
            <div className="breadcrumb">
              <Link to="/orders">Orders</Link> / Order Details
            </div>
            <h1>Order {order.id}</h1>
          </div>
          
          <div className="order-actions">
            <Link to="/orders" className="btn btn-secondary">
              {isMobile ? '← Orders' : '← Back to Orders'}
            </Link>
            {order.crmCallTarget && (
              <button
                type="button"
                className="btn btn-success order-detail-call-button"
                onClick={() => setCallTarget({
                  ...order.crmCallTarget,
                  preferredHubId: order.warehouse_id,
                })}
                title={`Call ${order.crmCallTarget.contactName} via phone or desktop`}
              >
                <span aria-hidden="true">☎</span>
                Call {order.crmCallTarget.contactName}
              </button>
            )}
            {order.crmWhatsappTarget && (
              <button
                type="button"
                className="btn crm-lead-whatsapp-action"
                onClick={() => setWhatsappTarget({
                  ...order.crmWhatsappTarget,
                  preferredHubId: order.warehouse_id,
                })}
                title={`WhatsApp ${order.crmWhatsappTarget.contactName}`}
              >
                WhatsApp {order.crmWhatsappTarget.contactName}
              </button>
            )}
            {isMobile && !isEditing && !order.confirmed && order.source !== 'ZS B2B' && (
              <button
                onClick={handleConfirmOrder}
                disabled={updating}
                className="btn btn-success"
              >
                {updating ? 'Confirming...' : 'Confirm Order'}
              </button>
            )}
            {isMobile && (
              <button type="button" className="btn btn-secondary" onClick={() => setHeaderMoreOpen((open) => !open)}>
                {headerMoreOpen ? 'Less ▴' : 'More ▾'}
              </button>
            )}
            {(!isMobile || headerMoreOpen) && <div
              ref={invoiceDropdownRef}
              style={{ position: 'relative' }}
            >
              <button
                className="btn btn-primary"
                onClick={() => setInvoiceDropdownOpen(o => !o)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                disabled={generatingInvoice || sendingWhatsApp}
              >
                Invoice Actions <span style={{ fontSize: '10px' }}>▼</span>
              </button>
              {invoiceDropdownOpen && (
                <div className="dropdown-menu" style={{ display: 'block', position: 'absolute', top: '100%', right: 0, zIndex: 1000, minWidth: '220px' }}>
                  <button
                    className="dropdown-item"
                    onClick={() => { setInvoiceDropdownOpen(false); handleDownloadInvoice(); }}
                    disabled={generatingInvoice}
                    style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: generatingInvoice ? 'not-allowed' : 'pointer' }}
                  >
                    {generatingInvoice ? 'Generating...' : '📄 Download Draft Invoice'}
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={handleSendWhatsAppTrigger}
                    disabled={sendingWhatsApp || whatsAppSendCount >= 1}
                    style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: (sendingWhatsApp || whatsAppSendCount >= 1) ? 'not-allowed' : 'pointer', opacity: (sendingWhatsApp || whatsAppSendCount >= 1) ? 0.6 : 1 }}
                  >
                    {sendingWhatsApp
                      ? 'Sending...'
                      : whatsAppSendCount >= 1
                        ? '💬 Send Draft Invoice (Limit reached)'
                        : '💬 Send Draft Invoice'}
                  </button>
                </div>
              )}
            </div>}
            {!isEditing ? (
              (!isMobile || headerMoreOpen) && <>
                {/* Hub Manager: generate directly (no request/approve step) for
                    GSTZen-eligible sources only. Shown for not-yet-generated
                    orders (empty or already 'requested'). */}
                {hubManagerAccess && order.invoice_request_status !== 'generated' &&
                  (order.source === 'Online' || order.source === 'ZS B2B' || order.source === 'Channel') && (
                  <button
                    onClick={() => setShowInvoiceGenerateModal(true)}
                    disabled={invoiceRequestLoading}
                    className="btn btn-success"
                  >
                    {invoiceRequestLoading ? 'Generating...' : 'Generate Invoice'}
                  </button>
                )}

                {/* Hub-to-hub: accounts users generate the invoice directly (no
                    request/approve step). Only shown when a target hub is set. */}
                {/* Hub-to-hub INTERSTATE (different state): tax invoice / e-invoice. */}
                {order.source === 'hub to hub' && accountsAccess && !hubManagerAccess &&
                  order.invoice_request_status !== 'generated' && order.target_hub_id &&
                  order.hub_to_hub_interstate === true && (
                  <button
                    onClick={() => setShowInvoiceGenerateModal(true)}
                    disabled={invoiceRequestLoading}
                    className="btn btn-success"
                  >
                    {invoiceRequestLoading ? 'Generating...' : 'Generate Invoice'}
                  </button>
                )}

                {/* Hub-to-hub INTRASTATE (same state): Delivery Challan, no invoice. */}
                {order.source === 'hub to hub' && accountsAccess && !hubManagerAccess &&
                  order.invoice_request_status !== 'generated' && order.target_hub_id &&
                  order.hub_to_hub_interstate === false && (
                  <button
                    onClick={handleGenerateDeliveryChallan}
                    disabled={invoiceRequestLoading}
                    className="btn btn-success"
                  >
                    {invoiceRequestLoading ? 'Generating...' : 'Generate DC'}
                  </button>
                )}

                {/* Standard flow (non hub-manager): request, then approve & generate */}
                {!hubManagerAccess && !order.invoice_request_status && (order.source === 'Online' || order.source === 'ZS B2B' || order.source === 'Channel') && (
                  <button
                    onClick={handleRequestInvoice}
                    disabled={invoiceRequestLoading}
                    className="btn btn-info"
                    style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8' }}
                  >
                    {invoiceRequestLoading ? 'Requesting...' : 'Request Invoice'}
                  </button>
                )}
                {!hubManagerAccess && order.invoice_request_status === 'requested' && accountsAccess && (
                  <button
                    onClick={() => setShowInvoiceGenerateModal(true)}
                    disabled={invoiceRequestLoading}
                    className="btn btn-success"
                  >
                    {invoiceRequestLoading ? 'Generating...' : 'Approve & Generate Invoice'}
                  </button>
                )}
                {order.invoice_request_status === 'generated' && (
                  <>
                    <span className="status-badge status-confirmed" style={{ padding: '8px 12px', fontSize: '12px' }}>
                      ✓ Invoice Generated
                    </span>

                    {/* Hub Manager: generate E-Way bill, but only for a GSTZen invoice
                        that THIS hub manager generated, and only if none exists yet. */}
                    {hubManagerAccess && generatedInvoice &&
                      generatedInvoice.raised_via === 'hub_manager' &&
                      generatedInvoice.approved_by === loggedInUserId &&
                      generatedInvoice.generation_method === 'gstzen' &&
                      !generatedInvoice.eway_bill_path &&
                      generatedInvoice.cancellation_status !== 'approved' && (
                      <button
                        onClick={handleGenerateEwayBill}
                        disabled={ewayLoading}
                        className="btn btn-warning"
                        style={{ fontSize: '12px' }}
                      >
                        {ewayLoading ? 'Generating...' : 'Gen E-Way'}
                      </button>
                    )}

                    {hubManagerAccess && generatedInvoice &&
                      generatedInvoice.raised_via === 'hub_manager' &&
                      generatedInvoice.approved_by === loggedInUserId &&
                      generatedInvoice.generation_method === 'gstzen' &&
                      generatedInvoice.eway_bill_path && (
                      <span
                        className="status-badge status-confirmed"
                        style={{ padding: '8px 12px', fontSize: '12px' }}
                      >
                        ✓ E-Way Generated
                      </span>
                    )}

                    {generatedInvoice && generatedInvoice.cancellation_status === 'none' &&
                      (new Date() - new Date(generatedInvoice.createdAt)) / (1000 * 60 * 60) <= 24 && (
                      <button
                        onClick={handleRequestCancellation}
                        disabled={cancellationLoading}
                        className="btn btn-danger"
                        style={{ fontSize: '12px' }}
                      >
                        {cancellationLoading ? 'Requesting...' : 'Request Cancellation'}
                      </button>
                    )}
                    {generatedInvoice && generatedInvoice.cancellation_status === 'requested' && (
                      <span className="status-badge" style={{ padding: '8px 12px', fontSize: '12px', backgroundColor: '#fff3cd', color: '#856404' }}>
                        ⏳ Cancellation Pending
                      </span>
                    )}
                    {generatedInvoice && generatedInvoice.cancellation_status === 'approved' && (
                      <span className="status-badge" style={{ padding: '8px 12px', fontSize: '12px', backgroundColor: '#f8d7da', color: '#721c24' }}>
                        ✗ Invoice Cancelled
                      </span>
                    )}
                  </>
                )}
                {!isMobile && !order.confirmed && order.source !== 'ZS B2B' && (
                  <button
                    onClick={handleConfirmOrder}
                    disabled={updating}
                    className="btn btn-success"
                  >
                    {updating ? 'Confirming...' : 'Confirm Order'}
                  </button>
                )}
                {order.source === 'ZS B2B' && order.status === 'ordered' && !order.confirmed && (
                  <>
                    <button
                      onClick={handleB2BConfirm}
                      disabled={updating}
                      className="btn btn-success"
                    >
                      {updating ? 'Confirming...' : 'B2B Confirm (Debit Balance)'}
                    </button>
                    <button
                      onClick={() => setShowB2BRejectModal(true)}
                      disabled={updating}
                      className="btn btn-danger"
                    >
                      Reject & Refund
                    </button>
                  </>
                )}
                {order.source === 'ZS B2B' && order.status === 'rejected' && (
                  <span className="status-badge" style={{ padding: '8px 12px', fontSize: '12px', backgroundColor: '#f8d7da', color: '#721c24' }}>
                    ✗ B2B Order Rejected
                  </span>
                )}
                {order.source === 'ZS B2B' && order.confirmed && (
                  <span className="status-badge" style={{ padding: '8px 12px', fontSize: '12px', backgroundColor: '#d4edda', color: '#155724' }}>
                    ✓ B2B Confirmed
                  </span>
                )}
                { (editAccess || accountsAccess) ? <button onClick={handleEditClick} className="btn btn-primary">
                  Edit Details
                </button> : "" }
                {editAccess && order && order.source === 'Online' && !['cancelled', 'delivered', 'truncated'].includes(order.status) && (
                  <button
                    onClick={handleCancelAndRefund}
                    disabled={updating}
                    className="btn btn-danger"
                    style={{ fontSize: '12px' }}
                  >
                    {updating ? 'Processing...' : 'Cancel & Refund'}
                  </button>
                )}
              </>
            ) : (
              <>
                <button onClick={handleSaveEdit} disabled={updating} className="btn btn-success">
                  {updating ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={handleCancelEdit} disabled={updating} className="btn btn-secondary">
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* Phones: status strip with the key facts and pending actions up top */}
        {isMobile && (
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
              <span className={getStatusBadgeClass(order.status)}>
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
              <span className={order.confirmed ? 'status-badge status-confirmed' : 'status-badge status-pending'}>
                {order.confirmed ? 'Confirmed' : 'Not confirmed'}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: '16px', fontWeight: '700', color: '#0d6efd' }}>
                ₹{parseFloat(order.totalAmount).toFixed(2)}
              </span>
            </div>
            {pendingActions.length > 0 && (
              <div style={{ marginTop: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '8px 10px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#dc3545', marginBottom: '4px' }}>
                  ⚠️ Pending Actions ({pendingActions.length})
                </div>
                <ul style={{ margin: 0, paddingLeft: '18px', listStyleType: 'disc' }}>
                  {pendingActions.map((action, index) => (
                    <li key={index} style={{ fontSize: '13px', color: '#856404', marginBottom: '4px', fontWeight: '500' }}>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Order Information - Three Column Layout */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
          gap: '12px',
          marginBottom: '12px'
        }}>
          {/* Column 1 */}
          <div style={{ 
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            padding: '12px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Order ID:</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{order.id}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Warehouse:</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{order.warehouse_name || 'N/A'}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Customer:</span>
                {(isEditing && editAccess && !hubManagerLimitedEdit) ? (
                  <input
                    type="text"
                    value={editedData.customerName}
                    onChange={(e) => handleFieldChange('customerName', e.target.value)}
                    style={{ fontSize: '11px', padding: '2px 4px', width: '150px', border: '1px solid #d1d5db', borderRadius: '3px' }}
                  />
                ) : (
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{order.customerName}</span>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Email:</span>
                {(isEditing && editAccess && (userRole === 'admin' || userRole === 'superAdmin')) ? (
                  <input
                    type="email"
                    value={editedData.customerEmail}
                    onChange={(e) => handleFieldChange('customerEmail', e.target.value)}
                    style={{ fontSize: '11px', padding: '2px 4px', width: '150px', border: '1px solid #d1d5db', borderRadius: '3px' }}
                  />
                ) : (userRole === 'admin' || userRole === 'superAdmin') ? (
                  <span style={{ fontSize: '11px', fontWeight: '500', color: '#4b5563' }}>{isMobile && order.customerEmail ? <a href={`mailto:${order.customerEmail}`}>{order.customerEmail}</a> : order.customerEmail}</span>
                ) : revealedEmail ? (
                  <span style={{ fontSize: '11px', fontWeight: '500', color: '#4b5563' }}>{isMobile ? <a href={`mailto:${revealedEmail}`}>{revealedEmail}</a> : revealedEmail}</span>
                ) : (
                  <button
                    onClick={() => handleRevealPii('email')}
                    disabled={piiLoading.email}
                    style={{ fontSize: isMobile ? '13px' : '10px', padding: isMobile ? '8px 12px' : '2px 6px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', opacity: piiLoading.email ? 0.6 : 1 }}
                  >
                    {piiLoading.email ? '...' : '👁 View Email'}
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Phone:</span>
                {(isEditing && editAccess && (userRole === 'admin' || userRole === 'superAdmin')) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <input
                      type="tel"
                      value={editedData.phone}
                      onChange={(e) => handleFieldChange('phone', e.target.value)}
                      style={{ 
                        fontSize: '11px', 
                        padding: '2px 4px', 
                        width: '150px', 
                        border: validationErrors.phone ? '1px solid #dc3545' : '1px solid #d1d5db', 
                        borderRadius: '3px' 
                      }}
                      maxLength="10"
                      placeholder="10 digits"
                    />
                    {validationErrors.phone && (
                      <span style={{ fontSize: '10px', color: '#dc3545' }}>{validationErrors.phone}</span>
                    )}
                  </div>
                ) : (userRole === 'admin' || userRole === 'superAdmin') ? (
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{isMobile && order.phone ? <a href={`tel:${order.phone}`}>{order.phone}</a> : order.phone}</span>
                ) : revealedPhone ? (
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{isMobile ? <a href={`tel:${revealedPhone}`}>{revealedPhone}</a> : revealedPhone}</span>
                ) : (
                  <button
                    onClick={() => handleRevealPii('phone')}
                    disabled={piiLoading.phone}
                    style={{ fontSize: isMobile ? '13px' : '10px', padding: isMobile ? '8px 12px' : '2px 6px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', opacity: piiLoading.phone ? 0.6 : 1 }}
                  >
                    {piiLoading.phone ? '...' : '👁 View Phone'}
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Order Date:</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>
                  {new Date(order.orderDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Delivery Date:</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>
                  {order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  }) : 'N/A'}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Shipping Date:</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>
                  {order.shippingDate ? new Date(order.shippingDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  }) : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Column 2 */}
          <div style={{ 
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            padding: '12px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Status:</span>
                <span className={getStatusBadgeClass(order.status)} style={{ fontSize: '10px', padding: '2px 6px' }}>
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Confirmed:</span>
                <span className={order.confirmed ? 'status-badge status-confirmed' : 'status-badge status-pending'} style={{ fontSize: '10px', padding: '2px 6px' }}>
                  {order.confirmed ? 'Yes' : 'No'}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Source:</span>
                {isEditing && !hubManagerLimitedEdit ? (
                  <select
                    value={editedData.source}
                    onChange={(e) => handleFieldChange('source', e.target.value)}
                    style={{ fontSize: '11px', padding: '2px 4px', width: '110px', border: '1px solid #d1d5db', borderRadius: '3px' }}
                  >
                    <option value="Online">Online</option>
                    <option value="Channel">Channel</option>
                    <option value="ZS B2C">ZS B2C</option>
                    <option value="ZS B2B">ZS B2B</option>
                    <option value="hub to hub">hub to hub</option>
                    <option value="damaged">damaged</option>
                  </select>
                ) : (
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{order.source}</span>
                )}
              </div>

              {/* Target Hub — only for hub-to-hub orders. Editable by accounts users only (never hub managers). */}
              {((isEditing ? editedData.source : order.source) === 'hub to hub') && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Target Hub:</span>
                  {isEditing && !hubManagerLimitedEdit && accountsAccess ? (
                    <select
                      value={editedData.target_hub_id}
                      onChange={(e) => handleFieldChange('target_hub_id', e.target.value)}
                      style={{ fontSize: '11px', padding: '2px 4px', width: '150px', border: '1px solid #d1d5db', borderRadius: '3px' }}
                    >
                      <option value="">Select target hub</option>
                      {hubs.map((h) => (
                        <option key={h.id} value={h.id}>{h.display_name || h.name}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>
                      {order.target_hub_name || 'N/A'}
                    </span>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Order Type:</span>
                {isEditing && !hubManagerLimitedEdit ? (
                  <select
                    value={editedData.deliveryDay}
                    onChange={(e) => handleFieldChange('deliveryDay', e.target.value)}
                    style={{ fontSize: '11px', padding: '2px 4px', width: '110px', border: '1px solid #d1d5db', borderRadius: '3px' }}
                  >
                    <option value="Delivery">Delivery</option>
                    <option value="self">Self Pickup</option>
                  </select>
                ) : (
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>
                    {(order.deliveryDay == 'self' || order.deliveryDay == 'selfTenDays') ? 'Self Pickup' : 'Delivery'}
                  </span>
                )}
              </div>

              {order.expectedDeliveryDate && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Expected Delivery:</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>
                    {new Date(order.expectedDeliveryDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}

              {order.gstNumber || isEditing ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>GST Number:</span>
                  {isEditing && !hubManagerLimitedEdit ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', width: '150px', flexShrink: 0 }}>
                      <input
                        type="text"
                        value={editedData.gstNumber}
                        onChange={(e) => handleFieldChange('gstNumber', e.target.value)}
                        style={{ 
                          fontSize: '11px', 
                          padding: '2px 4px', 
                          width: '100%', 
                          boxSizing: 'border-box',
                          border: validationErrors.gstNumber ? '1px solid #dc3545' : '1px solid #d1d5db', 
                          borderRadius: '3px', 
                          textTransform: 'uppercase' 
                        }}
                        maxLength="15"
                        placeholder="22AAAAA0000A1Z5"
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                      {validationErrors.gstNumber && (
                        <span style={{ fontSize: '10px', color: '#dc3545', textAlign: 'right', lineHeight: 1.3 }}>{validationErrors.gstNumber}</span>
                      )}
                    </div>
                  ) : (
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#111827' }}>{order.gstNumber}</span>
                  )}
                </div>
              ) : null}
              {(true) && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Vehicle No.:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.vehicle_number}
                        onChange={(e) => handleFieldChange('vehicle_number', e.target.value)}
                        style={{ fontSize: '11px', padding: '2px 4px', width: '150px', border: '1px solid #d1d5db', borderRadius: '3px' }}
                        placeholder="Vehicle number"
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                    ) : (
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#111827' }}>{order.vehicle_number || 'N/A'}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Transporter ID:</span>
                    {isEditing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', width: '150px', flexShrink: 0 }}>
                        <input
                          type="text"
                          value={editedData.transporter_id}
                          onChange={(e) => handleFieldChange('transporter_id', e.target.value.toUpperCase())}
                          style={{
                            fontSize: '11px',
                            padding: '2px 4px',
                            width: '100%',
                            boxSizing: 'border-box',
                            border: validationErrors.transporter_id ? '1px solid #dc3545' : '1px solid #d1d5db',
                            borderRadius: '3px',
                            textTransform: 'uppercase'
                          }}
                          maxLength="15"
                          placeholder="22AAAAA0000A1Z5"
                          autoCapitalize="characters"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                        {validationErrors.transporter_id && (
                          <span style={{ fontSize: '10px', color: '#dc3545', textAlign: 'right', lineHeight: 1.3 }}>{validationErrors.transporter_id}</span>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#111827' }}>{order.transporter_id || 'N/A'}</span>
                    )}
                  </div>
                  {/* Transporter Name is read-only here — it is resolved from the
                      transporter ID (or asked) during e-way bill generation. */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Transporter Name:</span>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#111827' }}>{order.transporter_name || 'N/A'}</span>
                  </div>
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Invoice No.:</span>
                {isEditing && !hubManagerLimitedEdit ? (
                  <input
                    type="text"
                    value={editedData.invoice_number}
                    onChange={(e) => handleFieldChange('invoice_number', e.target.value)}
                    style={{ fontSize: '11px', padding: '2px 4px', width: '150px', border: '1px solid #d1d5db', borderRadius: '3px' }}
                    placeholder="Invoice number"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                ) : (
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#111827' }}>{order.invoice_number || 'N/A'}</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Invoice Date:</span>
                {isEditing && !hubManagerLimitedEdit ? (
                  <input
                    type="date"
                    value={editedData.invoice_date}
                    onChange={(e) => handleFieldChange('invoice_date', e.target.value)}
                    style={{ fontSize: '11px', padding: '2px 4px', width: '150px', border: '1px solid #d1d5db', borderRadius: '3px' }}
                  />
                ) : (
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#111827' }}>
                    {order.invoice_date ? new Date(order.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Column 3 */}
          <div style={{ 
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            padding: '12px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Total Amount:</span>
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#0d6efd' }}>₹{parseFloat(order.totalAmount).toFixed(2)}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Amount Paid:</span>
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#059669' }}>
                  ₹{order.advance_amount ? parseFloat(order.advance_amount).toFixed(2) : "0.00"}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Payment Pending:</span>
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#dc2626' }}>
                  ₹{order.pending_payment ? parseFloat(order.pending_payment).toFixed(2) : "0.00"}
                </span>
              </div>

              {parseFloat(order.promo_wallet_used) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Promo Wallet Used:</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#6f42c1' }}>
                    ₹{parseFloat(order.promo_wallet_used).toFixed(2)}
                  </span>
                </div>
              )}

              {order.paymentMode && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Payment Mode:</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{order.paymentMode}</span>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Coupon:</span>
                <span style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: (order.couponName || order.coupon_name || parseFloat(order.couponDiscount) > 0) ? '#059669' : '#111827' 
                }}>
                  {order.couponName || order.coupon_name
                    ? (parseFloat(order.couponDiscount) > 0 
                        ? `${order.couponName || order.coupon_name} (₹${parseFloat(order.couponDiscount).toFixed(2)})` 
                        : (order.couponName || order.coupon_name))
                    : (parseFloat(order.couponDiscount) > 0 
                        ? `Applied (₹${parseFloat(order.couponDiscount).toFixed(2)})` 
                        : 'N/A')}
                </span>
              </div>

              {order.companyNameGST || isEditing ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Company:</span>
                  {isEditing && !hubManagerLimitedEdit ? (
                    <input
                      type="text"
                      value={editedData.companyNameGST}
                      onChange={(e) => handleFieldChange('companyNameGST', e.target.value)}
                      style={{ fontSize: '11px', padding: '2px 4px', width: '150px', border: '1px solid #d1d5db', borderRadius: '3px' }}
                    />
                  ) : (
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{order.companyNameGST}</span>
                  )}
                </div>
              ) : null}

              {order.platform && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Platform:</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{order.platform}</span>
                </div>
              )}

              {order.app_version && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>App Version:</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{order.app_version}</span>
                </div>
              )}

              {order.pending_payment_utr && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Pending UTR:</span>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#111827' }}>{order.pending_payment_utr}</span>
                </div>
              )}

              {order.source === 'ZS B2B' && (
                <>
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #e5e7eb' }}>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#7c3aed' }}>B2B Payment Details</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Advance Paid:</span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#059669' }}>
                      ₹{order.b2b_advance_amount ? parseFloat(order.b2b_advance_amount).toFixed(2) : '0.00'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Balance Remaining:</span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: order.confirmed ? '#059669' : '#dc2626' }}>
                      ₹{order.b2b_balance_amount ? parseFloat(order.b2b_balance_amount).toFixed(2) : '0.00'}
                    </span>
                  </div>
                  {order.b2b_confirmed_by && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Confirmed By:</span>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#111827' }}>{order.b2b_confirmed_by}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* OTP Verification Section */}
        {!otpVerified && (
          <div className="card">
            <h3 className="section-title">OTP Verification</h3>

            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="form-control"
                placeholder="Enter OTP"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                style={{ maxWidth: "200px" }}
                disabled={verifyingOtp}
              />

              <button
                className="btn btn-primary"
                onClick={handleVerifyOtp}
                disabled={verifyingOtp}
              >
                {verifyingOtp ? "Verifying..." : "Verify OTP"}
              </button>
            </div>

            {otpError && (
              <p style={{ color: "#dc3545", marginTop: "8px" }}>
                {otpError}
              </p>
            )}
          </div>
        )}

        {/* Pending Actions Section (phones show it in the status strip at the top) */}
        {!isMobile && (() => {
          // Only show section if there are pending actions
          if (pendingActions.length === 0) {
            return null;
          }
          
          return (
            <div className="card">
              <h3 className="section-title" style={{ color: '#dc3545' }}>
                ⚠️ Pending Actions ({pendingActions.length})
              </h3>
              <div style={{ 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffc107',
                borderRadius: '6px',
                padding: '16px'
              }}>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: '20px',
                  listStyleType: 'disc'
                }}>
                  {pendingActions.map((action, index) => (
                    <li key={index} style={{ 
                      fontSize: '14px', 
                      color: '#856404',
                      marginBottom: '8px',
                      fontWeight: '500'
                    }}>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })()}

        {/* Shipping Address */}
        <div className="card">
          <h3 className="section-title">Shipping Address</h3>
          {isEditing ? (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '8px' }}>
              <div>
                <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Location/Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={editedData.shipping_address_location}
                  onChange={(e) => handleFieldChange('shipping_address_location', e.target.value)}
                  placeholder="Enter location/address"
                  style={{ fontSize: '11px', padding: '4px 6px', height: '30px' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>City</label>
                <input
                  type="text"
                  className="form-control"
                  value={editedData.shipping_address_city}
                  onChange={(e) => handleFieldChange('shipping_address_city', e.target.value)}
                  placeholder="Enter city"
                  style={{ fontSize: '11px', padding: '4px 6px', height: '30px' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>State</label>
                <input
                  type="text"
                  className="form-control"
                  value={editedData.shipping_address_state}
                  onChange={(e) => handleFieldChange('shipping_address_state', e.target.value)}
                  placeholder="Enter state"
                  style={{ fontSize: '11px', padding: '4px 6px', height: '30px' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Pincode</label>
                <input
                  type="text"
                  className="form-control"
                  value={editedData.shipping_address_pincode}
                  onChange={(e) => handleFieldChange('shipping_address_pincode', e.target.value)}
                  placeholder="Enter pincode"
                  style={{ fontSize: '11px', padding: '4px 6px', height: '30px' }}
                />
              </div>
            </div>
          ) : (
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#495057' }}>
                {[
                  order.shipping_address_location,
                  order.shipping_address_city,
                  order.shipping_address_state,
                  order.shipping_address_pincode
                ].filter(Boolean).join(', ') || 'No shipping address provided'}
              </p>
              {isMobile && shippingAddressText && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shippingAddressText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', marginTop: '8px', fontSize: '13px', fontWeight: '600' }}
                >
                  Open in Maps
                </a>
              )}
            </div>
          )}
        </div>

        {/* Billing Address */}
        <div className="card">
          <h3 className="section-title">Billing Address</h3>
          {isEditing ? (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '8px' }}>
              <div>
                <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Location/Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={editedData.billing_address_location}
                  onChange={(e) => handleFieldChange('billing_address_location', e.target.value)}
                  placeholder="Enter location/address"
                  style={{ fontSize: '11px', padding: '4px 6px', height: '30px' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>City</label>
                <input
                  type="text"
                  className="form-control"
                  value={editedData.billing_address_city}
                  onChange={(e) => handleFieldChange('billing_address_city', e.target.value)}
                  placeholder="Enter city"
                  style={{ fontSize: '11px', padding: '4px 6px', height: '30px' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>State</label>
                <input
                  type="text"
                  className="form-control"
                  value={editedData.billing_address_state}
                  onChange={(e) => handleFieldChange('billing_address_state', e.target.value)}
                  placeholder="Enter state"
                  style={{ fontSize: '11px', padding: '4px 6px', height: '30px' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Pincode</label>
                <input
                  type="text"
                  className="form-control"
                  value={editedData.billing_address_pincode}
                  onChange={(e) => handleFieldChange('billing_address_pincode', e.target.value)}
                  placeholder="Enter pincode"
                  style={{ fontSize: '11px', padding: '4px 6px', height: '30px' }}
                />
              </div>
            </div>
          ) : (
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#495057' }}>
                {[
                  order.billing_address_location,
                  order.billing_address_city,
                  order.billing_address_state,
                  order.billing_address_pincode
                ].filter(Boolean).join(', ') || 'No billing address provided'}
              </p>
              {isMobile && billingAddressText && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(billingAddressText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', marginTop: '8px', fontSize: '13px', fontWeight: '600' }}
                >
                  Open in Maps
                </a>
              )}
            </div>
          )}
        </div>

        {/* Delivery Fee Adjustment (accounts/admin only, not for delivered/cancelled/truncated) */}
        {accountsAccess && order && !['delivered', 'cancelled', 'truncated'].includes(order.status) && !orderHistory.find(h => h.field_name === 'delivery_fee_adjustment') && (
          <div style={{ marginBottom: '15px', padding: '12px 16px', background: '#fff3cd', borderRadius: '6px', border: '1px solid #ffc107', display: 'flex', gap: '10px', alignItems: 'center', ...(isMobile ? { flexWrap: 'wrap' } : {}) }}>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>Delivery Fee Adjustment:</span>
            {(parseFloat(order.deliveryCharges) > 0 || parseFloat(order.unloadingCharges || 0) > 0) && (
              <button onClick={handleRemoveDeliveryFee} className="btn btn-danger" style={{ fontSize: '12px', padding: isMobile ? '8px 12px' : '4px 10px' }}>
                Remove Delivery Fee (₹{parseFloat(order.deliveryCharges || 0) + parseFloat(order.unloadingCharges || 0)}) & Refund
              </button>
            )}
            {(!order.deliveryCharges || parseFloat(order.deliveryCharges) === 0) && (!order.unloadingCharges || parseFloat(order.unloadingCharges) === 0) && (
              <button onClick={() => setShowAddDeliveryModal(true)} className="btn btn-primary" style={{ fontSize: '12px', padding: isMobile ? '8px 12px' : '4px 10px' }}>
                Add Delivery Fee & Deduct from Wallet
              </button>
            )}
          </div>
        )}
        {orderHistory.find(h => h.field_name === 'delivery_fee_adjustment') && (
          <div style={{ marginBottom: '15px', padding: '10px 16px', background: '#d4edda', borderRadius: '6px', border: '1px solid #28a745' }}>
            <span style={{ fontSize: '12px', color: '#155724' }}>✓ Delivery fee already adjusted for this order</span>
          </div>
        )}

        {/* Order Items */}
        <div className="card">
          <h3 className="section-title">Order Items</h3>
          <div className="order-items-table">
            <table>
              <thead>
                <tr>
                  <th>SKU Id</th>
                  <th>Product Name</th>
                  <th>Serial Numbers</th>
                  <th>Quantity</th>
                  <th>Unit Price (excl. GST)</th>
                  <th>Amount (excl. GST)</th>
                  <th>GST %</th>
                  <th>GST Amount</th>
                  <th style={{ textAlign: 'right' }}>Total (incl. GST)</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item) => {
                  const gstPercent = item.gstPercent || 5;
                  const priceExclGST = parseFloat(item.price);
                  const gstAmount = priceExclGST * (gstPercent / 100);
                  const priceInclGST = priceExclGST + gstAmount;
                  const totalExclGST = priceExclGST * item.quantity;
                  const totalGST = gstAmount * item.quantity;
                  const totalInclGST = priceInclGST * item.quantity;
                  const isKit = item.productType === 'kit' && item.kitItems && item.kitItems.length > 0;
                  
                  return (
                    <React.Fragment key={item.id}>
                      <tr style={item.isGift ? { backgroundColor: '#f0fdf4' } : (isKit ? { backgroundColor: '#f0f9ff' } : {})}>
                        <td style={{ fontWeight: '600' }}>
                          {item.skuCode}
                          {isKit && <span style={{ marginLeft: '6px', fontSize: '10px', background: '#0369a1', color: '#fff', borderRadius: '3px', padding: '1px 5px', verticalAlign: 'middle' }}>KIT</span>}
                        </td>
                        <td style={{ fontWeight: '600' }}>
                          {item.name}
                          {item.isGift && <span style={{ marginLeft: '6px', fontSize: '10px', background: '#16a34a', color: '#fff', borderRadius: '3px', padding: '1px 5px', verticalAlign: 'middle' }}>GIFT</span>}
                        </td>
                        <td style={{ width: '140px', minWidth: '140px' }}>
                          {!isKit && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                              {item.serialNumbers && item.serialNumbers.length > 0 ? (
                                <button
                                  onClick={() => handleSerialView(item)}
                                  style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '5px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px', color: '#0369a1', fontWeight: '600' }}
                                >
                                  <span style={{ background: '#0369a1', color: '#fff', borderRadius: '3px', padding: '0px 5px', fontSize: '10px' }}>{item.serialNumbers.length}</span>
                                  Serials
                                </button>
                              ) : (
                                <span style={{ fontSize: '11px', color: '#9ca3af', width: '60px', display: 'inline-block' }}>
                                  {item.dcrApplicable ? '—' : <span title="Optional">—</span>}
                                </span>
                              )}
                              {(editAccess || (order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled')) && (
                                <button
                                  onClick={() => handleSerialEdit(item)}
                                  style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap' }}
                                >
                                  {item.serialNumbers && item.serialNumbers.length > 0 ? 'Edit' : '+ Add'}
                                </button>
                              )}
                            </div>
                          )}
                          {isKit && <span style={{ fontSize: '11px', color: '#6b7280' }}>See below</span>}
                        </td>
                        <td>
                          <span style={{ 
                            background: '#e2e8f0', 
                            padding: '4px 8px', 
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {item.quantity}
                          </span>
                        </td>
                        <td>₹{priceExclGST.toFixed(2)}</td>
                        <td>₹{totalExclGST.toFixed(2)}</td>
                        <td>{gstPercent}%</td>
                        <td>₹{totalGST.toFixed(2)}</td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: '#0d6efd' }}>
                          ₹{totalInclGST.toFixed(2)}
                        </td>
                      </tr>
                      {/* Kit component sub-rows */}
                      {isKit && item.kitItems.map((ki, idx) => {
                        const kiGst = ki.gstPercent || gstPercent;
                        const kiPrice = parseFloat(ki.price);
                        const kiTotal = kiPrice * ki.qty;
                        const kiGstAmt = kiTotal * (kiGst / 100);
                        return (
                          <tr key={`kit-${item.id}-${idx}`} style={{ backgroundColor: '#f8fafc', borderLeft: '3px solid #0369a1' }}>
                            <td style={{ paddingLeft: '20px', fontSize: '12px', color: '#475569' }}>{ki.skuCode || '—'}</td>
                            <td style={{ paddingLeft: '20px', fontSize: '12px', color: '#475569' }}>↳ {ki.productName}</td>
                            <td style={{ width: '140px', minWidth: '140px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                                {ki.serialNumbers && ki.serialNumbers.length > 0 ? (
                                  <button
                                    onClick={() => handleKitSerialView(ki, item.id)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '5px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px', color: '#0369a1', fontWeight: '600' }}
                                  >
                                    <span style={{ background: '#0369a1', color: '#fff', borderRadius: '3px', padding: '0px 5px', fontSize: '10px' }}>{ki.serialNumbers.length}</span>
                                    Serials
                                  </button>
                                ) : (
                                  <span style={{ fontSize: '11px', color: '#9ca3af', width: '60px', display: 'inline-block' }}>—</span>
                                )}
                                {(editAccess || (order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled')) && (
                                  <button
                                    onClick={() => handleKitSerialEdit(ki, item.id)}
                                    style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap' }}
                                  >
                                    {ki.serialNumbers && ki.serialNumbers.length > 0 ? 'Edit' : '+ Add'}
                                  </button>
                                )}
                              </div>
                            </td>
                            <td>
                              <span style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '500' }}>
                                {ki.qty}
                              </span>
                            </td>
                            <td style={{ fontSize: '12px', color: '#475569' }}>₹{kiPrice.toFixed(2)}</td>
                            <td style={{ fontSize: '12px', color: '#475569' }}>₹{kiTotal.toFixed(2)}</td>
                            <td style={{ fontSize: '12px', color: '#475569' }}>{kiGst}%</td>
                            <td style={{ fontSize: '12px', color: '#475569' }}>₹{kiGstAmt.toFixed(2)}</td>
                            <td style={{ textAlign: 'right', fontSize: '12px', color: '#475569' }}>₹{(kiTotal + kiGstAmt).toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                
                {/* Delivery Charges Row */}
                {parseFloat(order.unloadingCharges || 0) > 0 && (() => {
                  const gstPercent = 18;
                  const amountInclGST = parseFloat(order.deliveryCharges) + parseFloat(order.unloadingCharges);
                  const amountExclGST = amountInclGST / (1 + gstPercent / 100);
                  const gstAmount = amountInclGST - amountExclGST;
                  
                  return (
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <td colSpan="2" style={{ fontWeight: '600', fontStyle: 'italic' }}>
                        Delivery With Unloading Charges
                      </td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>₹{amountExclGST.toFixed(2)}</td>
                      <td>{gstPercent}%</td>
                      <td>₹{gstAmount.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontWeight: '600', color: '#0d6efd' }}>
                        ₹{amountInclGST.toFixed(2)}
                      </td>
                    </tr>
                  );
                })()}

                {parseFloat(order.deliveryCharges) > 0 && parseFloat(order.unloadingCharges || 0) <= 0 && (() => {
                  const gstPercent = 18;
                  const amountInclGST = parseFloat(order.deliveryCharges);
                  const amountExclGST = amountInclGST / (1 + gstPercent / 100);
                  const gstAmount = amountInclGST - amountExclGST;
                  
                  return (
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <td colSpan="2" style={{ fontWeight: '600', fontStyle: 'italic' }}>
                        Delivery Without Unloading Charges
                      </td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>₹{amountExclGST.toFixed(2)}</td>
                      <td>{gstPercent}%</td>
                      <td>₹{gstAmount.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontWeight: '600', color: '#0d6efd' }}>
                        ₹{amountInclGST.toFixed(2)}
                      </td>
                    </tr>
                  );
                })()}
                
                {/* Handling Charges Row */}
                {parseFloat(order.handlingCharges) > 0 && (() => {
                  const gstPercent = 18;
                  const amountInclGST = parseFloat(order.handlingCharges);
                  const amountExclGST = amountInclGST / (1 + gstPercent / 100);
                  const gstAmount = amountInclGST - amountExclGST;
                  
                  return (
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <td colSpan="2" style={{ fontWeight: '600', fontStyle: 'italic' }}>
                        Handling Charges
                      </td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>₹{amountExclGST.toFixed(2)}</td>
                      <td>{gstPercent}%</td>
                      <td>₹{gstAmount.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontWeight: '600', color: '#0d6efd' }}>
                        ₹{amountInclGST.toFixed(2)}
                      </td>
                    </tr>
                  );
                })()}

                {/* Platform Charges Row */}
                {parseFloat(order.platformCharges) > 0 && (() => {
                  const gstPercent = 18;
                  const amountInclGST = parseFloat(order.platformCharges);
                  const amountExclGST = amountInclGST / (1 + gstPercent / 100);
                  const gstAmount = amountInclGST - amountExclGST;
                  
                  return (
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <td colSpan="2" style={{ fontWeight: '600', fontStyle: 'italic' }}>
                        Platform Charges
                      </td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>₹{amountExclGST.toFixed(2)}</td>
                      <td>{gstPercent}%</td>
                      <td>₹{gstAmount.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontWeight: '600', color: '#0d6efd' }}>
                        ₹{amountInclGST.toFixed(2)}
                      </td>
                    </tr>
                  );
                })()}
                
                {/* Discount Row */}
                {parseFloat(order.couponDiscount) > 0 && (
                  <tr style={{ backgroundColor: '#fff3cd' }}>
                    <td colSpan="2" style={{ fontWeight: '600', fontStyle: 'italic', color: '#856404' }}>
                      Discount {order.couponName && `(${order.couponName})`}
                    </td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td colSpan="3" style={{ textAlign: 'right', fontWeight: '600', color: '#dc3545' }}>
                      
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: '600', color: '#dc3545' }}>
                      -₹{parseFloat(order.couponDiscount).toFixed(2)}
                    </td>
                  </tr>
                )}

                {/* Kit Discount Row (incl. GST) */}
                {parseFloat(order.kitDiscount) > 0 && (
                  <tr style={{ backgroundColor: '#fff3cd' }}>
                    <td colSpan="2" style={{ fontWeight: '600', fontStyle: 'italic', color: '#856404' }}>
                      Kit Discount
                    </td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td colSpan="3" style={{ textAlign: 'right', fontWeight: '600', color: '#dc3545' }}>
                      
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: '600', color: '#dc3545' }}>
                      -₹{parseFloat(order.kitDiscount).toFixed(2)}
                    </td>
                  </tr>
                )}


                
                {/* Totals Row */}
                <tr style={{ 
                  borderTop: '2px solid #dee2e6',
                  backgroundColor: '#e7f3ff',
                  fontWeight: '700'
                }}>
                  <td colSpan="4" style={{ fontSize: '16px', padding: '12px 8px' }}>
                    Grand Total
                  </td>
                  <td></td>
                  <td style={{ fontSize: '15px', padding: '12px 8px' }}>
                    ₹{(() => {
                      let total = 0;
                      
                      // Sum of all product amounts excluding GST
                      order.items.forEach(item => {
                        const priceExclGST = parseFloat(item.price);
                        total += priceExclGST * item.quantity;
                      });
                      
                      // Delivery charges are including GST, so calculate excl. GST
                      if (parseFloat(order.deliveryCharges) > 0) {
                        const amountInclGST = parseFloat(order.deliveryCharges);
                        const amountExclGST = amountInclGST / 1.18;
                        total += amountExclGST;
                      }
                      
                      // Handling charges are including GST, so calculate excl. GST
                      if (parseFloat(order.handlingCharges) > 0) {
                        const amountInclGST = parseFloat(order.handlingCharges);
                        const amountExclGST = amountInclGST / 1.18;
                        total += amountExclGST;
                      }

                      // Platform charges are including GST, so calculate excl. GST
                      if (parseFloat(order.platformCharges) > 0) {
                        const amountInclGST = parseFloat(order.platformCharges);
                        const amountExclGST = amountInclGST / 1.18;
                        total += amountExclGST;
                      }
                      
                      return total.toFixed(2);
                    })()}
                  </td>
                  <td></td>
                  <td style={{ fontSize: '15px', padding: '12px 8px' }}>
                    ₹{(() => {
                      let total = 0;
                      
                      // Sum of all product GST amounts
                      order.items.forEach(item => {
                        const gstPercent = item.gstPercent || 5;
                        const priceExclGST = parseFloat(item.price);
                        const gstAmount = priceExclGST * (gstPercent / 100);
                        total += gstAmount * item.quantity;
                      });
                      
                      // Delivery charges GST (already including GST, so extract GST portion)
                      if (parseFloat(order.deliveryCharges) > 0) {
                        const amountInclGST = parseFloat(order.deliveryCharges);
                        const amountExclGST = amountInclGST / 1.18;
                        total += amountInclGST - amountExclGST;
                      }
                      
                      // Handling charges GST (already including GST, so extract GST portion)
                      if (parseFloat(order.handlingCharges) > 0) {
                        const amountInclGST = parseFloat(order.handlingCharges);
                        const amountExclGST = amountInclGST / 1.18;
                        total += amountInclGST - amountExclGST;
                      }

                      // Platform charges GST (already including GST, so extract GST portion)
                      if (parseFloat(order.platformCharges) > 0) {
                        const amountInclGST = parseFloat(order.platformCharges);
                        const amountExclGST = amountInclGST / 1.18;
                        total += amountInclGST - amountExclGST;
                      }
                      
                      return total.toFixed(2);
                    })()}
                  </td>
                  <td style={{ textAlign: 'right', fontSize: '16px', color: '#0d6efd', padding: '12px 8px' }}>
                    ₹{parseFloat(order.totalAmount).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* File Management Section */}
        <div className="card">
          <h3 className="section-title">Order Documents</h3>
          
          {/* File Upload Section */}
          <div className="file-upload-section">
            <div style={{ display: 'flex', gap: '15px', alignItems: 'end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ minWidth: '200px' }}>
                <label className="form-label">Document Type</label>
                <select
                  value={selectedFileType}
                  onChange={(e) => setSelectedFileType(e.target.value)}
                  className="form-control"
                >
                  <option value="Invoice">Invoice</option>
                  <option value="E-Way Bill">E-Way Bill</option>
                  <option value="DCR">DCR</option>
                  <option value="DC">DC</option>
                </select>
              </div>
              
              <div className="form-group" style={{ minWidth: '250px' }}>
                <label className="form-label">Select File</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="form-control"
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={uploading}
                />
              </div>
              
              <button
                className="btn btn-primary uploadFileButton"
                disabled={uploading}
                style={{ height: '42px' }}
                onClick={handleFileUpload}
              >
                {uploading ? 'Uploading...' : 'Upload File'}
              </button>
            </div>
            
            {/* <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '20px' }}>
              Supported formats: PDF, JPEG, PNG (Max size: 10MB)
            </div> */}
          </div>

          {/* Files Table */}
          <div className="files-table-container">
            <DataTable
              columns={[
                {
                  name: 'File Name',
                  selector: row => row.file_name,
                  sortable: true,
                  cell: row => (
                    <div style={{ fontWeight: '500', color: '#0d6efd' }}>
                      {row.file_name}
                    </div>
                  )
                },
                {
                  name: 'Document Type',
                  selector: row => row.file_type,
                  sortable: true,
                  cell: row => (
                    <span className={getFileTypeBadgeClass(row.file_type)}>
                      {getFileTypeLabel(row.file_type)}
                    </span>
                  )
                },
                {
                  name: 'File Size',
                  selector: row => row.file_size,
                  sortable: true
                },
                {
                  name: 'Upload Date',
                  selector: row => row.created_at,
                  sortable: true,
                  cell: row => (
                    <div>
                      {new Date(row.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                      <br />
                      <small style={{ color: '#6c757d' }}>
                        {new Date(row.created_at).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </small>
                    </div>
                  )
                },
                {
                  name: 'Uploaded By',
                  selector: row => row.uploaded_by,
                  sortable: true
                },
                {
                  name: 'Actions',
                  cell: row => (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                        onClick={() => handleDownloadFile(row)}
                      >
                        Download
                      </button>
                      {['Invoice', 'E-Way Bill'].includes(row.file_type) && (
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                          onClick={() => handlePrintFile(row)}
                          disabled={printingFileId === row.id}
                        >
                          {printingFileId === row.id ? 'Preparing…' : 'Print'}
                        </button>
                      )}
                      <button
                        className="btn btn-secondary"
                        style={{ 
                          fontSize: '12px', 
                          padding: '4px 8px', 
                          backgroundColor: '#dc3545', 
                          borderColor: '#dc3545',
                          color: 'white'
                        }}
                        onClick={() => handleDeleteFile(row.id)}
                      >
                        Delete
                      </button>
                    </div>
                  ),
                  ignoreRowClick: true
                }
              ]}
              data={uploadedFiles}
              pagination
              paginationPerPage={5}
              paginationRowsPerPageOptions={[5, 10, 15]}
              noDataComponent={
                <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>No files uploaded</div>
                  <div>Upload documents related to this order using the form above</div>
                </div>
              }
              customStyles={{
                table: {
                  style: {
                    backgroundColor: 'white',
                  },
                },
                headRow: {
                  style: {
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6',
                  },
                },
                headCells: {
                  style: {
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '14px',
                  },
                },
                rows: {
                  style: {
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Status Update Section */}
        <div className="card">
          <h3 className="section-title">Update Order Status</h3>
          <div className="status-update-section">
            <div className="status-update-form">
              <div className="form-group">
                <label className="form-label">New Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="form-control"
                >
                  <option value="ordered">Ordered</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="truncated">Truncated</option>
                </select>
              </div>
              <button
                onClick={handleStatusChange}
                disabled={updating || newStatus === order.status}
                className="btn btn-warning"
              >
                {updating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
        {isMobile && isEditing && (
          <div className="m-sticky-actions">
            <button onClick={handleSaveEdit} disabled={updating} className="btn btn-success">
              {updating ? 'Saving...' : 'Save Changes'}
            </button>
            <button onClick={handleCancelEdit} disabled={updating} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Invoice Details Modal */}
      {showInvoiceModal && (
        <div
          onClick={() => { setShowInvoiceModal(false); setPendingFileUpload(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '10px', width: 'min(420px, 95vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Invoice Details Required</div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>Please fill in invoice details before uploading</div>
              </div>
              <button
                onClick={() => { setShowInvoiceModal(false); setPendingFileUpload(false); }}
                style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '18px', cursor: 'pointer', color: '#6b7280', lineHeight: 1, ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}
              >×</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                  Invoice Number <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  value={invoiceModalData.invoice_number}
                  onChange={e => setInvoiceModalData(prev => ({ ...prev, invoice_number: e.target.value }))}
                  placeholder="Enter invoice number"
                  style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                  Invoice Date <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="date"
                  value={invoiceModalData.invoice_date}
                  onChange={e => setInvoiceModalData(prev => ({ ...prev, invoice_date: e.target.value }))}
                  style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <button
                onClick={() => { setShowInvoiceModal(false); setPendingFileUpload(false); }}
                style={{ fontSize: '13px', padding: '7px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
              >Cancel</button>
              <button
                onClick={handleSaveInvoiceDetails}
                disabled={savingInvoiceDetails}
                style={{ fontSize: '13px', padding: '7px 16px', background: '#0d6efd', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '600', opacity: savingInvoiceDetails ? 0.7 : 1 }}
              >{savingInvoiceDetails ? 'Saving...' : 'Save & Upload'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Generation Modal */}
      {showInvoiceGenerateModal && (
        <div
          onClick={() => setShowInvoiceGenerateModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '10px', width: 'min(400px, 95vw)', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>Generate Invoice</div>
              <button
                onClick={() => setShowInvoiceGenerateModal(false)}
                style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '18px', cursor: 'pointer', color: '#6b7280', lineHeight: 1, ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}
              >×</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                  Order ID
                </label>
                <input
                  type="text"
                  value={orderId}
                  disabled
                  style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box', backgroundColor: '#f3f4f6' }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <button
                onClick={() => setShowInvoiceGenerateModal(false)}
                disabled={invoiceRequestLoading}
                style={{ fontSize: '13px', padding: '7px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
              >Cancel</button>
              <button
                onClick={handleApproveAndGenerateInvoice}
                disabled={invoiceRequestLoading}
                style={{ fontSize: '13px', padding: '7px 16px', background: '#28a745', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '600', opacity: invoiceRequestLoading ? 0.7 : 1 }}
              >{invoiceRequestLoading ? 'Generating...' : 'Generate Invoice'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Serial Numbers Modal */}
      {serialModalItem && (
        <div
          onClick={handleSerialModalClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '10px', width: 'min(460px, 95vw)', height: 'min(520px, 90vh)', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>Serial Numbers</div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>{serialModalItem.name}</div>
              </div>
              <button onClick={handleSerialModalClose} style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '18px', cursor: 'pointer', color: '#6b7280', lineHeight: 1, ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}>×</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '16px 18px', maxHeight: '420px', overflowY: 'auto', flex: 1 }}>
              {serialEditingItemId === serialModalItem.id ? (
                (() => {
                  const isKit = serialModalItem.categoryId === 4;

                  const maxSerials = isKit ? null : serialModalItem.quantity;
                  const inputs = serialInputs[serialModalItem.id] || [''];
                  // Task 2: find duplicates
                  const counts = {};
                  inputs.forEach(v => { const t = v.trim(); if (t) counts[t] = (counts[t] || 0) + 1; });
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {inputs.map((sn, idx) => {
                        const isDup = sn.trim() !== '' && counts[sn.trim()] > 1;
                        return (
                          <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', color: '#9ca3af', width: '20px', textAlign: 'right', flexShrink: 0 }}>{idx + 1}.</span>
                            {serialImages[sn.trim()] && (
                              <img
                                src={serialImages[sn.trim()]}
                                alt="scan reference"
                                onClick={() => setPreviewImage({ src: serialImages[sn.trim()], serial: sn.trim() })}
                                title="Click to view"
                                style={{ width: '34px', height: '26px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0, border: '1px solid #e5e7eb', cursor: 'pointer' }}
                              />
                            )}
                            <input
                              type="text"
                              value={sn}
                              ref={el => serialInputRefs.current[idx] = el}
                              onChange={(e) => handleSerialInputChangeWithAutoAdd(serialModalItem.id, idx, e.target.value, maxSerials)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  // focus next input if it exists, otherwise the last one is the new empty row
                                  const next = serialInputRefs.current[idx + 1];
                                  if (next) next.focus();
                                }
                              }}
                              placeholder={`Serial number ${idx + 1}`}
                              autoCapitalize="characters"
                              autoCorrect="off"
                              spellCheck={false}
                              autoComplete="off"
                              style={{
                                flex: 1, fontSize: isMobile ? '16px' : '12px', padding: isMobile ? '10px 10px' : '5px 8px', borderRadius: '5px', fontFamily: 'monospace',
                                border: isDup ? '1px solid #ef4444' : '1px solid #d1d5db',
                                background: isDup ? '#fff5f5' : '#fff',
                                color: isDup ? '#ef4444' : 'inherit',
                              }}
                              autoFocus={idx === 0}
                            />
                            <button
                              onClick={() => handleRemoveSerialInput(serialModalItem.id, idx)}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: isMobile ? '22px' : '16px', lineHeight: 1, padding: '0 2px', flexShrink: 0, ...(isMobile ? { minWidth: '36px', minHeight: '36px' } : {}) }}
                              title="Remove"
                            >×</button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {serialModalItem.serialNumbers && serialModalItem.serialNumbers.length > 0 ? (
                    serialModalItem.serialNumbers.map((sn, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', background: '#f8fafc', borderRadius: '5px', border: '1px solid #e5e7eb' }}>
                        <span style={{ fontSize: '11px', color: '#9ca3af', width: '20px', textAlign: 'right', flexShrink: 0 }}>{idx + 1}.</span>
                        {serialImages[(sn || '').trim()] && (
                          <img
                            src={serialImages[(sn || '').trim()]}
                            alt="scan reference"
                            onClick={() => setPreviewImage({ src: serialImages[(sn || '').trim()], serial: (sn || '').trim() })}
                            title="Click to view"
                            style={{ width: '34px', height: '26px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0, border: '1px solid #e5e7eb', cursor: 'pointer' }}
                          />
                        )}
                        <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#0369a1', fontWeight: '600' }}>{sn}</span>
                      </div>
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: '13px' }}>No serial numbers added yet</div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              {serialEditingItemId === serialModalItem.id ? (
                (() => {
                  const isKit = serialModalItem.categoryId === 4;
                  const maxSerials = isKit ? null : serialModalItem.quantity;
                  const inputs = serialInputs[serialModalItem.id] || [''];
                  const counts = {};
                  inputs.forEach(v => { const t = v.trim(); if (t) counts[t] = (counts[t] || 0) + 1; });
                  const hasDuplicates = Object.values(counts).some(c => c > 1);
                  // Limit by how many serials are actually filled, not row count
                  // (an empty placeholder row shouldn't disable Add / Scan).
                  const filledCount = inputs.filter(v => v.trim() !== '').length;
                  const atMax = maxSerials != null && filledCount >= maxSerials;
                  return (
                    <>
                      <button
                        onClick={() => handleAddSerialInput(serialModalItem.id)}
                        disabled={atMax}
                        style={{ fontSize: '12px', padding: '6px 12px', background: '#fff', border: '1px dashed #d1d5db', borderRadius: '6px', cursor: atMax ? 'not-allowed' : 'pointer', color: atMax ? '#9ca3af' : '#374151', marginRight: 'auto', opacity: atMax ? 0.5 : 1 }}
                      >+ Add Serial</button>
                      <button
                        onClick={() => openItemScanner(serialModalItem)}
                        disabled={atMax}
                        style={{ fontSize: '12px', padding: '6px 12px', background: '#0d6efd', border: 'none', borderRadius: '6px', cursor: atMax ? 'not-allowed' : 'pointer', color: '#fff', fontWeight: '600', opacity: atMax ? 0.5 : 1 }}
                        title="Scan QR / barcode with camera"
                      >📷 Scan</button>

                      {hasDuplicates && (
                        <span style={{ fontSize: '11px', color: '#ef4444', alignSelf: 'center' }}>Duplicate serial numbers</span>
                      )}
                      <button onClick={handleSerialCancel} style={{ fontSize: '12px', padding: '6px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}>Cancel</button>
                      <button
                        onClick={() => handleSerialSave(serialModalItem.id)}
                        disabled={serialSaving || hasDuplicates}
                        style={{ fontSize: '12px', padding: '6px 14px', background: '#0d6efd', border: 'none', borderRadius: '6px', cursor: hasDuplicates ? 'not-allowed' : 'pointer', color: '#fff', fontWeight: '600', opacity: (serialSaving || hasDuplicates) ? 0.6 : 1 }}
                      >{serialSaving ? 'Saving...' : 'Save'}</button>
                    </>
                  );
                })()
              ) : (
                <>
                  <button onClick={handleSerialModalClose} style={{ fontSize: '12px', padding: '6px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}>Close</button>
                  {(editAccess || (order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled')) && (
                    <button
                      onClick={() => handleSerialEdit(serialModalItem)}
                      style={{ fontSize: '12px', padding: '6px 14px', background: '#0d6efd', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '600' }}
                    >Edit</button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Kit Item Serial Numbers Modal */}
      {kitSerialModalItem && (
        <div
          onClick={handleKitSerialModalClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '10px', width: 'min(460px, 95vw)', height: 'min(520px, 90vh)', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>Serial Numbers</div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>{kitSerialModalItem.productName} <span style={{ color: '#9ca3af' }}>· Qty: {kitSerialModalItem.qty}</span></div>
              </div>
              <button onClick={handleKitSerialModalClose} style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '18px', cursor: 'pointer', color: '#6b7280', lineHeight: 1, ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}>×</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '16px 18px', maxHeight: '420px', overflowY: 'auto', flex: 1 }}>
              {kitSerialEditingId === kitSerialModalItem.id ? (
                (() => {
                  const inputs = kitSerialInputs[kitSerialModalItem.id] || [''];
                  const counts = {};
                  inputs.forEach(v => { const t = v.trim(); if (t) counts[t] = (counts[t] || 0) + 1; });
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {inputs.map((sn, idx) => {
                        const isDup = sn.trim() !== '' && counts[sn.trim()] > 1;
                        return (
                          <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', color: '#9ca3af', width: '20px', textAlign: 'right', flexShrink: 0 }}>{idx + 1}.</span>
                            {serialImages[sn.trim()] && (
                              <img
                                src={serialImages[sn.trim()]}
                                alt="scan reference"
                                onClick={() => setPreviewImage({ src: serialImages[sn.trim()], serial: sn.trim() })}
                                title="Click to view"
                                style={{ width: '34px', height: '26px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0, border: '1px solid #e5e7eb', cursor: 'pointer' }}
                              />
                            )}
                            <input
                              type="text"
                              value={sn}
                              ref={el => kitSerialInputRefs.current[idx] = el}
                              onChange={(e) => handleKitSerialInputChange(kitSerialModalItem.id, idx, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const next = kitSerialInputRefs.current[idx + 1];
                                  if (next) next.focus();
                                }
                              }}
                              placeholder={`Serial number ${idx + 1}`}
                              autoCapitalize="characters"
                              autoCorrect="off"
                              spellCheck={false}
                              autoComplete="off"
                              style={{
                                flex: 1, fontSize: isMobile ? '16px' : '12px', padding: isMobile ? '10px 10px' : '5px 8px', borderRadius: '5px', fontFamily: 'monospace',
                                border: isDup ? '1px solid #ef4444' : '1px solid #d1d5db',
                                background: isDup ? '#fff5f5' : '#fff',
                                color: isDup ? '#ef4444' : 'inherit',
                              }}
                              autoFocus={idx === 0}
                            />
                            <button
                              onClick={() => handleKitSerialRemoveInput(kitSerialModalItem.id, idx)}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: isMobile ? '22px' : '16px', lineHeight: 1, padding: '0 2px', flexShrink: 0, ...(isMobile ? { minWidth: '36px', minHeight: '36px' } : {}) }}
                              title="Remove"
                            >×</button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {kitSerialModalItem.serialNumbers && kitSerialModalItem.serialNumbers.length > 0 ? (
                    kitSerialModalItem.serialNumbers.map((sn, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', background: '#f8fafc', borderRadius: '5px', border: '1px solid #e5e7eb' }}>
                        <span style={{ fontSize: '11px', color: '#9ca3af', width: '20px', textAlign: 'right', flexShrink: 0 }}>{idx + 1}.</span>
                        {serialImages[(sn || '').trim()] && (
                          <img
                            src={serialImages[(sn || '').trim()]}
                            alt="scan reference"
                            onClick={() => setPreviewImage({ src: serialImages[(sn || '').trim()], serial: (sn || '').trim() })}
                            title="Click to view"
                            style={{ width: '34px', height: '26px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0, border: '1px solid #e5e7eb', cursor: 'pointer' }}
                          />
                        )}
                        <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#0369a1', fontWeight: '600' }}>{sn}</span>
                      </div>
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: '13px' }}>No serial numbers added yet</div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              {kitSerialEditingId === kitSerialModalItem.id ? (
                (() => {
                  const inputs = kitSerialInputs[kitSerialModalItem.id] || [''];
                  const maxSerials = kitSerialModalItem.qty;
                  const counts = {};
                  inputs.forEach(v => { const t = v.trim(); if (t) counts[t] = (counts[t] || 0) + 1; });
                  const hasDuplicates = Object.values(counts).some(c => c > 1);
                  // Limit by filled serials, not row count (empty placeholder
                  // row shouldn't disable Add / Scan).
                  const filledCount = inputs.filter(v => v.trim() !== '').length;
                  const atMax = maxSerials != null && filledCount >= maxSerials;
                  return (
                    <>
                      <button
                        onClick={() => handleKitSerialAddInput(kitSerialModalItem.id)}
                        disabled={atMax}
                        style={{ fontSize: '12px', padding: '6px 12px', background: '#fff', border: '1px dashed #d1d5db', borderRadius: '6px', cursor: atMax ? 'not-allowed' : 'pointer', color: atMax ? '#9ca3af' : '#374151', marginRight: 'auto', opacity: atMax ? 0.5 : 1 }}
                      >+ Add Serial</button>
                      <button
                        onClick={() => openKitScanner(kitSerialModalItem)}
                        disabled={atMax}
                        style={{ fontSize: '12px', padding: '6px 12px', background: '#0d6efd', border: 'none', borderRadius: '6px', cursor: atMax ? 'not-allowed' : 'pointer', color: '#fff', fontWeight: '600', opacity: atMax ? 0.5 : 1 }}
                        title="Scan QR / barcode with camera"
                      >📷 Scan</button>

                      {hasDuplicates && (
                        <span style={{ fontSize: '11px', color: '#ef4444', alignSelf: 'center' }}>Duplicate serial numbers</span>
                      )}
                      <button onClick={handleKitSerialCancel} style={{ fontSize: '12px', padding: '6px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}>Cancel</button>
                      <button
                        onClick={() => handleKitSerialSave(kitSerialModalItem.id, kitSerialModalItem.parentItemId)}
                        disabled={kitSerialSaving || hasDuplicates}
                        style={{ fontSize: '12px', padding: '6px 14px', background: '#0d6efd', border: 'none', borderRadius: '6px', cursor: hasDuplicates ? 'not-allowed' : 'pointer', color: '#fff', fontWeight: '600', opacity: (kitSerialSaving || hasDuplicates) ? 0.6 : 1 }}
                      >{kitSerialSaving ? 'Saving...' : 'Save'}</button>
                    </>
                  );
                })()
              ) : (
                <>
                  <button onClick={handleKitSerialModalClose} style={{ fontSize: '12px', padding: '6px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}>Close</button>
                  {(editAccess || (order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled')) && (
                    <button
                      onClick={() => handleKitSerialEdit(kitSerialModalItem, kitSerialModalItem.parentItemId)}
                      style={{ fontSize: '12px', padding: '6px 14px', background: '#0d6efd', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '600' }}
                    >Edit</button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Camera Scanner (QR / barcode -> serial number) */}
      {scannerTarget && (() => {
        const list = scannerTarget.kind === 'item'
          ? (serialInputs[scannerTarget.itemId] || [])
          : (kitSerialInputs[scannerTarget.kitItemId] || []);
        const filled = list.filter((s) => (s || '').trim() !== '').length;
        const max = scannerTarget.max;
        const subtitle = max != null ? `${filled} of ${max} scanned` : `${filled} scanned`;
        return (
          <CodeScannerDialog
            open
            title="Scan serial number"
            subtitle={subtitle}
            onScan={handleScannedCode}
            onClose={() => setScannerTarget(null)}
          />
        );
      })()}

      {/* Scan reference image preview (click a thumbnail to open full size) */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <img
            src={previewImage.src}
            alt="scan reference"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
          />
          {previewImage.serial ? (
            <div style={{ marginTop: '12px', color: '#fff', fontSize: '13px', fontFamily: 'monospace', fontWeight: '600' }}>{previewImage.serial}</div>
          ) : null}
          <button
            onClick={() => setPreviewImage(null)}
            style={{ marginTop: '14px', fontSize: '13px', padding: '8px 20px', background: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#111827', fontWeight: '600' }}
          >Close</button>
        </div>
      )}

      {/* Delivery Fee Adjustment — moved to above order items */}

      {/* Order History */}
      {orderHistory.length > 0 && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h3 className="section-title">Order History</h3>
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '10px', textAlign: 'left' }}>Time</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Who</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Field</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Old Value</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>New Value</th>
              </tr>
            </thead>
            <tbody>
              {orderHistory.map(h => (
                <tr key={h.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px' }}>{new Date(h.created_at || h.createdAt).toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td style={{ padding: '8px' }}>{h.changed_by}</td>
                  <td style={{ padding: '8px', fontWeight: '600' }}>{h.field_name}</td>
                  <td style={{ padding: '8px', color: '#dc3545' }}>{h.old_value || '-'}</td>
                  <td style={{ padding: '8px', color: '#28a745' }}>{h.new_value || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Add Delivery Fee Modal */}
      {showAddDeliveryModal && (
        <div className="modal-overlay" onClick={() => setShowAddDeliveryModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header"><h3>Add Delivery Fee</h3><button className="modal-close" onClick={() => setShowAddDeliveryModal(false)}>×</button></div>
            <div className="modal-body">
              <p style={{ fontSize: '13px', color: '#6c757d' }}>This amount will be deducted from the user's wallet.</p>
              <div className="info-item">
                <label className="info-label">Delivery Fee Amount (₹)</label>
                <input type="number" className="form-control" value={addDeliveryAmount} onChange={e => setAddDeliveryAmount(e.target.value)} placeholder="Enter amount" min="1" step="0.01" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddDeliveryModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddDeliveryFee}>Add & Deduct</button>
            </div>
          </div>
        </div>
      )}

      {/* B2B Reject Modal */}
      {showB2BRejectModal && (
        <div className="modal-overlay" onClick={() => setShowB2BRejectModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header"><h3>Reject B2B Order</h3><button className="modal-close" onClick={() => setShowB2BRejectModal(false)}>×</button></div>
            <div className="modal-body">
              <p>Rejecting this order will refund the advance amount (₹{order?.b2b_advance_amount}) to the buyer's wallet.</p>
              <div className="info-item">
                <label className="info-label">Reason (optional)</label>
                <textarea className="form-control" value={b2bRejectReason} onChange={e => setB2BRejectReason(e.target.value)} rows="3" placeholder="Reason for rejection" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowB2BRejectModal(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleB2BReject} disabled={updating}>{updating ? 'Rejecting...' : 'Reject & Refund'}</button>
            </div>
          </div>
        </div>
      )}

      {/* B2B Inventory Shortage Prompt */}
      {b2bInventoryPrompt && (
        <div className="modal-overlay" onClick={() => !updating && setB2BInventoryPrompt(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <h3>Insufficient Inventory</h3>
              <button className="modal-close" onClick={() => setB2BInventoryPrompt(null)} disabled={updating}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '10px' }}>
                Some items don't have enough stock in this warehouse. You can still confirm the
                order and debit the buyer's wallet, but inventory will go negative for these items.
              </p>
              <div style={{ border: '1px solid #f3f4f6', borderRadius: '6px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f9fafb', fontSize: '11px', fontWeight: 600, color: '#6b7280' }}>
                  <span>SKU</span>
                  <span>Available / Required</span>
                </div>
                {(b2bInventoryPrompt.shortages || []).map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', fontSize: '12px', borderTop: '1px solid #f3f4f6' }}>
                    <span style={{ fontWeight: 500 }}>{s.skuCode}</span>
                    <span><span style={{ color: '#dc2626', fontWeight: 700 }}>{s.available}</span> / {s.required}</span>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '12px', color: '#374151', marginTop: '12px' }}>
                Do you still want to proceed and confirm this order?
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setB2BInventoryPrompt(null)} disabled={updating}>Cancel</button>
              <button className="btn btn-success" onClick={handleB2BConfirmIgnoreInventory} disabled={updating}>
                {updating ? 'Confirming...' : 'Proceed & Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {callTarget && (
        <CrmCallDialog
          leadId={callTarget.leadId}
          contact={{ id: callTarget.contactId, name: callTarget.contactName }}
          hubLinks={callTarget.hubLinks || []}
          preferredHubId={callTarget.preferredHubId}
          orderId={callTarget.orderId || order.id}
          onClose={() => setCallTarget(null)}
          onLogged={() => setCallTarget(null)}
        />
      )}
      {whatsappTarget && (
        <CrmWhatsappDialog
          target={whatsappTarget}
          preferredHubId={whatsappTarget.preferredHubId}
          onClose={() => setWhatsappTarget(null)}
        />
      )}
    </div>
  );
};

export default OrderDetail;
